import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { WidgetCard } from "@/components/bi/WidgetCard";
import { WidgetRenderer } from "@/components/bi/WidgetRenderer";
import { Inspector } from "@/components/bi/Inspector";
import { AiDrawer } from "@/components/bi/AiDrawer";
import { FilterBar, toActiveFilters, clearDescendants, defaultFilterValues } from "@/components/bi/FilterBar";
import { FilterPanel, FilterPanelToggle, FilterLayoutSwitch } from "@/components/bi/FilterPanel";
import { useFilterSets } from "@/hooks/useFilterSets";
import { CreateRelationshipDialog } from "@/components/bi/CreateRelationshipDialog";
import { inPanel, downloadFilteredCsv } from "@/lib/filterPanel";
import { DashboardTabs } from "@/components/bi/DashboardTabs";
import { TabsManagerDialog } from "@/components/bi/TabsManager";
import {
  normalizeTabs, normalizeBar, initialTabId, rememberTab, widgetTabId, tabCanvas,
  applyTabOps, tabWidgetCounts, openableTabs, defaultTabId,
} from "@/lib/dashboardTabs";
import { themedWidgets, themedCanvas } from "@/lib/aiTheme";
import { CommandPalette } from "@/components/bi/CommandPalette";
import { BuilderTopbar } from "@/components/bi/BuilderTopbar";
import { PublishNoteDialog, ShareLinkDialog, TemplateSaveDialog } from "@/components/bi/BuilderDialogs";
import { buildBuilderCommands } from "@/lib/builderCommands";
import { AccessDialog, EmbedDialog, AddFilterDialog } from "@/components/bi/DashboardDialogs";
import { CanvasSettingsDialog } from "@/components/bi/CanvasSettings";
import { WidgetLibrary } from "@/components/bi/WidgetLibrary";
import { LayersPane } from "@/components/bi/LayersPane";
import { MultiSelectToolbar } from "@/components/bi/MultiSelectToolbar";
import { ShortcutSheet } from "@/components/bi/ShortcutSheet";
import { ResizableAside, usePanel } from "@/components/bi/ResizableAside";
import { DataPane } from "@/components/bi/DataPane";
import { readFieldDrag, FIELD_MIME } from "@/lib/fieldWells";
import { Loading } from "@/components/bi/Loading";
import { EmptyState } from "@/components/bi/EmptyState";
import { interactionFilters, splitInteractionFilters, slicerFilters, buildSemanticQuery, widgetDatasetIds, isCrossDatasetWidget, getWidgetMeta } from "@/lib/widgetCatalog";
import { FILTER_WIDGET_TYPES, filterWidgetToFilters } from "@/components/bi/FilterWidgets";
import { setFormatLocale } from "@/lib/chartTheme";
import { setLanguage } from "@/lib/i18n";
import { normalizeCanvas, isFixedCanvas, mobileLayoutFor, pushRecentWidget, effectiveWhatIfParams } from "@/lib/canvasUtils";
import { alignWidgets, expandToGroup, copyWidgetsToClipboard, readWidgetClipboard, materializePaste, remapWidgetDataset, newWid, newGid } from "@/lib/builderOps";
import { mergeAiWidgets } from "@/lib/widgetSanitize";
import { WidgetErrorBoundary, DashboardErrorBoundary } from "@/components/bi/ErrorBoundaries";
import { DatasetRemapDialog } from "@/components/bi/DatasetRemapDialog";
import { hasRelationshipPath } from "@/lib/relationships";
import { InteractionsPanel } from "@/components/bi/InteractionsPanel";
import { applyProfilePatch } from "@/components/bi/ProfileSwitcher";
import { UnderlyingDataDialog } from "@/components/bi/UnderlyingDataDialog";
import { VersionHistoryDialog } from "@/components/bi/VersionHistoryDialog";
import { DataViewPanel, ModelWorkspacePanel } from "@/components/bi/WorkspaceViews";
import { DataSourcesPanel } from "@/components/bi/DataSourcesPanel";
import { ContainerBody } from "@/components/bi/ContainerWidget";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Save, Eye, EyeOff, GripVertical, LayoutGrid, Download, FileImage, FileText, Code2, X, Smartphone, ZoomIn, ZoomOut, Maximize, Lock, Unlock, CopyPlus, ListTree, Keyboard, LayoutTemplate, Database, Table2, Paintbrush, RefreshCw, Server, Layers as LayersIcon, ArrowRightLeft, } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { exportWidget } from "@/lib/widgetExport";
import { WidgetViewActions } from "@/components/bi/WidgetViewActions";
import { ScheduleDialog } from "@/components/bi/ScheduleDialog";
import { DashboardChatPanel } from "@/components/bi/DashboardChatPanel";
import { CommentsPanel } from "@/components/bi/CommentsPanel";
import { PerfAnalyzerPanel } from "@/components/bi/PerfAnalyzerPanel";
import { A11yAuditPanel } from "@/components/bi/A11yAuditPanel";
import { decodeDrill } from "@/lib/drillthrough";
import { DrillthroughBar } from "@/components/bi/DrillthroughBar";
import { registerNavState, readNavState, applyScroll, setModalChrome } from "@/lib/navState";
import { loadFilterState, saveFilterState, hasFilterState } from "@/lib/filterStatePersist";
import { useRegional } from "@/context/RegionalContext";
import { useAuth } from "@/context/AuthContext";
import { WhatIfBar } from "@/components/bi/WhatIfBar";
import { paramFilters, resolveParamTokens } from "@/lib/paramEngine";
import { cn } from "@/lib/utils";
import { DEFAULT_SIZE, GRID, DROPPING_ID, defaultSizeFor, minSizeFor, clampLayout, pxToGrid, containerPointToGrid, containerUnderPoint, isWidgetDrag, readWidgetDrag, placeGroupAt, resolveFree } from "@/lib/layoutEngine";

const SIZE = DEFAULT_SIZE;


// Widgets start EMPTY — the author intentionally picks fields in the properties panel.
function makeEmptyConfig(type) {
  const options = { palette: "Eazy Core", colorByCategory: true, gradient: true, showLegend: true, showGrid: true, showLabels: false, smooth: true };
  if (type === "text") return { text: "" };
  if (type === "container") return { container: { layout: "free", gap: 8, padding: 8 }, options: {} };
  if (type === "slicer") return { dimensions: [] };
  if (["combo_box", "list_box", "date_picker"].includes(type)) return { dimensions: [], options: {} };
  return { dimensions: [], measures: [], limit: 1000, options };
}

const RESIZE_HANDLES = ["s", "w", "e", "n", "sw", "nw", "se", "ne"];
const ROW_H = GRID.ROW_H, MARGIN = GRID.MARGIN, PAD = GRID.PAD;

// Widget types whose body has its own drag/scroll interactions — those keep
// header-only whole-surface dragging (body stays interactive).
const DRAG_BODY_EXCLUDE = new Set([
  "point_map", "geo_map", "bubble_map", "bar_3d", "scatter_3d", "perspective",
  "data_grid", "table", "pivot", "slicer", "multi_filter",
  "combo_box", "list_box", "date_picker", "container",
]);
// Elements that must never initiate a whole-surface drag.
const DRAG_IGNORE_SEL = "button, a, input, textarea, select, [role='menuitem'], [data-nodrag], .widget-drag, .child-drag-1, .child-drag-2, .child-drag-3, .react-resizable-handle, .leaflet-container, perspective-viewer";
const DRAG_THRESHOLD_PX = 5;
const DRAG_CANCEL_SEL = "button, a, input, textarea, select, [role='menuitem'], [data-nodrag], .react-resizable-handle";

export default function Builder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [contracts, setContracts] = useState([]);   // tenant relationships (Model view) for drop-time validation
  const [joinPrompt, setJoinPrompt] = useState(null); // guided join: { leftId, rightId, retry }
  const loadContracts = useCallback(() => api.get("/join-contracts").then(({ data }) => setContracts(Array.isArray(data) ? data : [])).catch(() => {}), []);
  useEffect(() => {
    loadContracts();
    const onFocus = () => loadContracts();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadContracts]);
  const [selectedIds, setSelectedIds] = useState([]);
  // Item 13: a modal drillthrough renders this page inside a dialog — read-only, no editor chrome
  const dtModalView = new URLSearchParams(window.location.search).get("dtmodal") === "1";
  const [preview, setPreview] = useState(dtModalView);
  const [aiOpen, setAiOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);
  const [a11yOpen, setA11yOpen] = useState(() => new URLSearchParams(window.location.search).get("a11y") === "1");
  const [a11yReport, setA11yReport] = useState(null);
  const [whatIfVals, setWhatIfVals] = useState({});
  const [whatIfCompare, setWhatIfCompare] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStats, setShareStats] = useState(null);
  useEffect(() => {
    if (shareOpen && dashboard?.id) {
      api.get(`/dashboards/${dashboard.id}/share-stats`).then(({ data }) => setShareStats(data)).catch(() => setShareStats(null));
    }
  }, [shareOpen, dashboard?.id]);
  const [accessOpen, setAccessOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [interactionsOpen, setInteractionsOpen] = useState(false);
  const [underlying, setUnderlying] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [wsView, setWsView] = useState("report");   // report | data | model
  const [zoom, setZoom] = useState(1);
  const [saveState, setSaveState] = useState("saved");
  const [leftTab, setLeftTab] = useState("widgets");
  const [slicerVals, setSlicerVals] = useState({});   // `${dataset_id}::${field}` -> value
  const [gfVals, setGfVals] = useState({});            // global filter bar: filterId -> value
  const [recentBump, setRecentBump] = useState(0);
  // Library drag (new widget from the left panel) + container drop-target highlight
  const [libDrag, setLibDrag] = useState(null);       // {type} | {template}
  const libDragRef = useRef(null);
  const [hoverContainer, setHoverContainer] = useState(null);
  const hoverContainerRef = useRef(null);
  // Paste-at-click: last empty-canvas click in grid units; consecutive pastes offset from it
  const pasteAnchorRef = useRef(null);
  const pasteCountRef = useRef(0);
  const dirtyRef = useRef(false);
  const saveTimer = useRef(null);
  const dashRef = useRef(null);
  const aiCheckpointRef = useRef(0);
  const leftPanel = usePanel("eip_panel_left", 224);
  const rightPanel = usePanel("eip_panel_right", 320);

  // Sticky dataset selection (Item 178): the dataset the author is actively working
  // with is carried across widget creation, AI drawer, data/model views and chat.
  const [activeDsId, setActiveDsId] = useState(null);
  useEffect(() => {
    try { setActiveDsId(localStorage.getItem(`eip_active_ds_${id}`) || null); } catch { /* private mode */ }
  }, [id]);
  const setActiveDataset = useCallback((dsId) => {
    if (!dsId) return;
    setActiveDsId(dsId);
    try { localStorage.setItem(`eip_active_ds_${id}`, dsId); } catch { /* private mode */ }
  }, [id]);

  useEffect(() => {
    Promise.all([api.get(`/dashboards/${id}`), api.get("/datasets")])
      .then(([d, ds]) => {
        setDashboard(d.data); setDatasets(ds.data);
        setGfVals(defaultFilterValues(d.data.global_filters || []));
        // Crash recovery: offer to restore unsaved local draft newer than the server copy
        try {
          const draft = JSON.parse(localStorage.getItem(`eip_draft_${id}`));
          if (draft?.ts && draft.ts > Date.parse(d.data.updated_at || 0) + 3000) setRecoveryDraft(draft);
        } catch { /* no draft */ }
      })
      .catch(() => { toast.error("Failed to load dashboard"); nav("/"); });
  }, [id, nav]);

  const widgets = useMemo(() => dashboard?.widgets || [], [dashboard]);
  const widgetsRef = useRef(widgets);
  widgetsRef.current = widgets;
  // Item 97 — tabs (pages): each tab owns its widgets, layout, canvas overrides and filters.
  const tabs = useMemo(() => normalizeTabs(dashboard || {}), [dashboard]);
  const canvasAll = useMemo(() => normalizeCanvas(dashboard?.canvas), [dashboard?.canvas]);
  const tabBar = useMemo(() => normalizeBar(canvasAll), [canvasAll]);
  const [activeTab, setActiveTab] = useState("");
  const [tabsOpen, setTabsOpen] = useState(false);
  const tabInitRef = useRef(null);
  useEffect(() => {
    if (!dashboard?.id || tabInitRef.current === dashboard.id) return;
    tabInitRef.current = dashboard.id;
    setActiveTab(initialTabId(dashboard.id, tabs, tabBar, new URLSearchParams(window.location.search).get("tab") || ""));
  }, [dashboard?.id, tabs, tabBar]);
  const effTab = tabs.some((t) => t.id === activeTab) ? activeTab : defaultTabId(tabs);
  const curTab = tabs.find((t) => t.id === effTab) || tabs[0];
  const tabCounts = useMemo(() => tabWidgetCounts(widgets, tabs), [widgets, tabs]);
  // Conditional/hidden tabs disappear in preview & for viewers (authors keep seeing everything).
  const allowedTabs = useMemo(() => openableTabs(tabs, { gfVals, params: whatIfVals }, !preview),
    [tabs, gfVals, whatIfVals, preview]);
  useEffect(() => {
    if (activeTab && allowedTabs.length && !allowedTabs.some((t) => t.id === activeTab)) setActiveTab(allowedTabs[0].id);
  }, [allowedTabs, activeTab]);
  const visibleWidgets = useMemo(() => widgets.filter((w) => !w.hidden && !w.parent_id && widgetTabId(w, tabs) === effTab), [widgets, tabs, effTab]);
  const selSet = new Set(selectedIds);
  const selected = selectedIds.length === 1 ? widgets.find((w) => w.id === selectedIds[0]) : null;
  useEffect(() => { dashRef.current = dashboard; }, [dashboard]);
  const canvas = useMemo(() => tabCanvas(canvasAll, curTab), [canvasAll, curTab]);
  // Batch 8: effective what-if params (defaults + overrides) and baseline (defaults only)
  const whatIfEff = effectiveWhatIfParams(canvas.what_if, whatIfVals);
  const whatIfBase = effectiveWhatIfParams(canvas.what_if, {});
  const fixed = isFixedCanvas(canvas);
  const cols = canvas.grid_cols;
  const sizeScale = cols / 12;
  const activeDatasetId = (activeDsId && datasets.some((d) => d.id === activeDsId))
    ? activeDsId
    : (widgets.find((w) => w.dataset_id)?.dataset_id || datasets[0]?.id || null);
  useEffect(() => { setFormatLocale(canvas.locale); setLanguage(canvas.language); return () => { setFormatLocale(""); setLanguage(""); }; }, [canvas.locale, canvas.language]);

  const markDirty = () => {
    dirtyRef.current = true; setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(doSave, 1200);
  };

  // Crash-recovery draft: mirror unsaved changes to localStorage until a save succeeds.
  const [recoveryDraft, setRecoveryDraft] = useState(null);
  useEffect(() => {
    if (!dashboard || !dirtyRef.current) return;
    try {
      localStorage.setItem(`eip_draft_${id}`, JSON.stringify({
        ts: Date.now(), name: dashboard.name, widgets: dashboard.widgets,
        global_filters: dashboard.global_filters || [], canvas: dashboard.canvas, tabs: dashboard.tabs || [],
      }));
    } catch { /* storage full */ }
  }, [dashboard, id]);

  const doSave = async () => {
    const d = dashRef.current;
    if (!d) return;
    try {
      await api.put(`/dashboards/${d.id}`, {
        name: d.name, widgets: d.widgets, filters: d.filters || [],
        global_filters: d.global_filters || [], canvas: normalizeCanvas(d.canvas),
        tabs: d.tabs || [],
      });
      setSaveState("saved"); dirtyRef.current = false;
      try { localStorage.removeItem(`eip_draft_${d.id}`); } catch { /* ignore */ }
    } catch (e) {
      setSaveState("error");
      toast.error(e?.response?.data?.detail || "Save failed — your changes are kept locally");
    }
  };

  const applyRecovery = () => {
    const dr = recoveryDraft;
    setDashboard((d) => ({ ...d, name: dr.name ?? d.name, widgets: dr.widgets || d.widgets, global_filters: dr.global_filters || d.global_filters, canvas: dr.canvas || d.canvas, tabs: dr.tabs?.length ? dr.tabs : d.tabs }));
    setRecoveryDraft(null); markDirty();
    toast.success("Unsaved changes restored");
  };
  const discardRecovery = () => { try { localStorage.removeItem(`eip_draft_${id}`); } catch { } setRecoveryDraft(null); };

  // ---- Publish / lifecycle -------------------------------------------------
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [publishNoteOpen, setPublishNoteOpen] = useState(false);
  const [publishNote, setPublishNote] = useState("");
  const status = dashboard?.status || "development";
  const archived = status === "archived";
  const hasUnpublished = status === "published" && dashboard?.updated_at && dashboard?.published_at && dashboard.updated_at > dashboard.published_at;
  const doPublish = async (note = "") => {
    try {
      if (dirtyRef.current) await doSave();
      const { data } = await api.post(`/dashboards/${id}/publish`, { note });
      setDashboard((d) => ({ ...d, ...data }));
      setPublishNoteOpen(false); setPublishNote("");
      toast.success("Published — viewers and public links now see this version");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.a11y) {
        // Item 19: tenant accessibility policy blocked the publish — open the findings
        setA11yReport(det.a11y); setPublishNoteOpen(false); setA11yOpen(true);
        toast.error(det.message || "Accessibility policy blocked publishing");
        return;
      }
      if (det && typeof det === "object" && det.drillthrough) {
        // Item 13: broken drillthrough targets — select the first offending widget
        const first = det.drillthrough.issues.find((i) => i.severity === "error");
        setPublishNoteOpen(false);
        if (first?.widget_id) setSelectedIds([first.widget_id]);
        toast.error(det.message || "Drillthrough validation blocked publishing", { description: first ? `${first.widget_title}: ${first.message}` : undefined, duration: 8000 });
        return;
      }
      toast.error(typeof det === "string" ? det : "Publish failed");
    }
  };
  const openPublishDialog = async () => {
    if (dirtyRef.current) await doSave();   // diff must compare the freshest saved state
    setPublishNoteOpen(true);
  };
  const doUnpublish = async () => {
    try { const { data } = await api.post(`/dashboards/${id}/unpublish`); setDashboard((d) => ({ ...d, ...data })); toast.success("Moved back to Development"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const restoreFromArchive = async () => {
    try { const { data } = await api.post(`/dashboards/${id}/status`, { status: "development" }); setDashboard((d) => ({ ...d, ...data })); toast.success("Dashboard restored to Development"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ---- Undo / Redo -------------------------------------------------------
  const undoRef = useRef([]);
  const redoRef = useRef([]);
  const [histBump, setHistBump] = useState(0);
  const pushHistory = useCallback((current) => {
    undoRef.current = [...undoRef.current.slice(-49), JSON.stringify(current)];
    redoRef.current = [];
    setHistBump((b) => b + 1);
  }, []);
  const undo = () => {
    const prev = undoRef.current.pop();
    if (!prev) return;
    redoRef.current.push(JSON.stringify(widgets));
    setDashboard((d) => ({ ...d, widgets: JSON.parse(prev) }));
    setHistBump((b) => b + 1); markDirty();
  };
  const redo = () => {
    const next = redoRef.current.pop();
    if (!next) return;
    undoRef.current.push(JSON.stringify(widgets));
    setDashboard((d) => ({ ...d, widgets: JSON.parse(next) }));
    setHistBump((b) => b + 1); markDirty();
  };

  const updateWidgets = (newWidgets, { history = true } = {}) => {
    if (history) pushHistory(widgetsRef.current);
    // Item 97: anything created while a tab is open belongs to that tab.
    const known = new Set(widgetsRef.current.map((w) => w.id));
    const stamped = newWidgets.map((w) => (w.tab_id || known.has(w.id) ? w : { ...w, tab_id: effTab }));
    setDashboard((d) => ({ ...d, widgets: stamped }));
    markDirty();
  };

  const updateCanvas = (patch) => { setDashboard((d) => ({ ...d, canvas: { ...normalizeCanvas(d.canvas), ...patch } })); markDirty(); };

  // Apply a saved theme: canvas look + default palette on every chart widget
  const applyTheme = (t) => {
    const c = t.canvas || {};
    updateCanvas({ bg_color: c.bg_color ?? "", wallpaper_url: c.wallpaper_url ?? "", wallpaper_opacity: c.wallpaper_opacity ?? 0.35, locale: c.locale || "" });
    const pal = t.widget_defaults?.palette;
    if (pal) {
      setDashboard((d) => ({
        ...d,
        widgets: (d.widgets || []).map((w) => ["text", "slicer"].includes(w.type) ? w
          : ({ ...w, config: { ...(w.config || {}), options: { ...((w.config || {}).options || {}), palette: pal } } })),
      }));
      markDirty();
    }
    toast.success(`Theme “${t.name}” applied`);
  };
  const changeGridCols = (newCols) => {
    const old = canvas.grid_cols;
    if (newCols === old) return;
    const r = newCols / old;
    pushHistory(widgets);
    setDashboard((d) => ({
      ...d,
      canvas: { ...normalizeCanvas(d.canvas), grid_cols: newCols },
      widgets: (d.widgets || []).map((w) => ({
        ...w,
        layout: { ...(w.layout || {}), x: Math.round((w.layout?.x || 0) * r), w: Math.max(1, Math.round((w.layout?.w || 6) * r)) },
      })),
    }));
    markDirty();
  };

  // ---- Item 97: tab navigation & operations ---------------------------------
  const selectTab = (tid) => {
    if (!tid) return;
    setActiveTab(tid);
    setSelectedIds([]);
    if (dashboard?.id) rememberTab(dashboard.id, tid);
    const u = new URL(window.location.href);
    u.searchParams.set("tab", tid);
    window.history.replaceState({}, "", u.pathname + u.search);
  };
  const applyTabsResult = (res) => {
    pushHistory(widgetsRef.current);
    setDashboard((d) => ({ ...d, tabs: res.tabs, widgets: res.widgets }));
    markDirty();
    if (res.activeHint) selectTab(res.activeHint);
  };
  const runTabOps = (ops) => applyTabsResult(applyTabOps(tabs, widgetsRef.current, ops, cols));
  const setTabBar = (patch) => updateCanvas({ tabs_bar: { ...tabBar, ...patch } });
  const moveWidgetToTab = (w, tid, copy = false) => {
    runTabOps([{ op: copy ? "copy_widget" : "move_widget", widget: w.id, tab: tid }]);
    toast.success(`${copy ? "Copied" : "Moved"} to \u201c${(tabs.find((t) => t.id === tid) || {}).name}\u201d`);
  };

  // ---- Item 101: AI restyle (preview \u2192 apply / discard, with one-click undo) ----
  const [themePreview, setThemePreview] = useState(null);
  const [aiThemeScope, setAiThemeScope] = useState("dashboard");
  const [themeUndo, setThemeUndo] = useState(null);
  const applyAiTheme = () => {
    if (!themePreview) return;
    const snap = { widgets: JSON.stringify(widgetsRef.current), canvas: JSON.stringify(dashRef.current?.canvas || {}) };
    pushHistory(widgetsRef.current);
    setDashboard((d) => ({
      ...d,
      widgets: themedWidgets(d.widgets || [], themePreview),
      canvas: themedCanvas(normalizeCanvas(d.canvas), themePreview),
    }));
    setThemeUndo(snap);
    setThemePreview(null);
    markDirty();
    toast.success("Restyle applied");
  };
  const undoAiTheme = () => {
    if (!themeUndo) return;
    setDashboard((d) => ({ ...d, widgets: JSON.parse(themeUndo.widgets), canvas: JSON.parse(themeUndo.canvas) }));
    setThemeUndo(null);
    markDirty();
    toast.success("Restyle undone");
  };

  const makeWidget = (type, layout) => {
    const ds = datasets.find((d) => d.id === activeDatasetId) || datasets[0];
    return {
      id: newWid(), type, title: type === "container" ? "Container" : "",
      dataset_id: ["text", "image", "container"].includes(type) ? null : ds?.id || null,
      config: makeEmptyConfig(type),
      layout: clampLayout(layout, type, cols),
    };
  };
  // Click in the library → append at the bottom (legacy). Drag/drop passes an explicit position.
  const addWidget = (type, at = null, pushedLayouts = null) => {
    const size = defaultSizeFor(type, cols);
    const maxY = widgets.reduce((m, w) => Math.max(m, (w.layout?.y || 0) + (w.layout?.h || 4)), 0);
    const newW = makeWidget(type, { x: at ? at.x : 0, y: at ? at.y : maxY, w: size.w, h: size.h });
    let base = widgets;
    if (pushedLayouts) base = widgets.map((w) => pushedLayouts[w.id] && !w.parent_id ? { ...w, layout: { ...w.layout, ...pushedLayouts[w.id] } } : w);
    if (at && freeMode) {
      const placed = resolveFree({ [newW.id]: newW.layout }, base.filter((w) => !w.hidden && !w.parent_id).map((w) => ({ ...w.layout, i: w.id })), cols);
      newW.layout = { ...newW.layout, ...placed[newW.id] };
    }
    pushRecentWidget(type); setRecentBump((b) => b + 1);
    updateWidgets([...base, newW]);
    setSelectedIds([newW.id]);
    return newW;
  };
  // Drop a NEW widget (from the library) inside a Combine container at the pointer.
  const addWidgetIntoContainer = (type, cont, bodyEl, cx, cy) => {
    if (type === "container" && chainAbove(cont.id) + 2 > MAX_NEST) { toast.error(`Max container nesting is ${MAX_NEST} levels`); return null; }
    const size = defaultSizeFor(type, 12);
    const cc = (cont.config || {}).container || {};
    const lc = containerPointToGrid(cx, cy, bodyEl.getBoundingClientRect(), cc, size.w, size.h, zoom);
    const newW = { ...makeWidget(type, { x: 0, y: 0, w: size.w, h: size.h }), parent_id: cont.id, layout_child: lc };
    const mode = cc.layout || "free";
    if (mode === "free" && !cc.overlap) {
      const sibs = widgets.filter((w) => w.parent_id === cont.id && !w.hidden).map((w) => ({ ...(w.layout_child || { x: 0, y: 0, w: 6, h: 4 }), i: w.id }));
      const placed = resolveFree({ [newW.id]: lc }, sibs, 12);
      newW.layout_child = { ...lc, ...placed[newW.id] };
    }
    pushRecentWidget(type); setRecentBump((b) => b + 1);
    updateWidgets([...widgets, newW]);
    setSelectedIds([newW.id]);
    toast.success(`${getWidgetMetaSafe(type)} added to the container`);
    return newW;
  };
  const getWidgetMetaSafe = (type) => { try { return getWidgetMeta(type).label; } catch { return "Widget"; } };
  // Client pixel → grid coordinates on the desktop canvas (accounts for zoom).
  const gridPointFromClient = (cx, cy, size = { w: 1, h: 1 }) => {
    const el = document.getElementById("dashboard-capture");
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return pxToGrid((cx - r.left) / zoom, (cy - r.top) / zoom, { cols, width: gridW, rowH: ROW_H, margin: MARGIN, pad: PAD, w: size.w, h: size.h });
  };
  const endLibDrag = () => {
    libDragRef.current = null; setLibDrag(null);
    hoverContainerRef.current = null; setHoverContainer(null);
  };
  const startLibDrag = (p) => { libDragRef.current = p; setLibDrag(p); };
  const trackHoverContainer = (cx, cy, exclude = null, movingIds = []) => {
    const hit = containerUnderPoint(cx, cy, exclude);
    const ok = hit && !movingIds.includes(hit.id) && widgets.some((w) => w.id === hit.id && w.type === "container");
    const cid = ok ? hit.id : null;
    if (hoverContainerRef.current !== cid) { hoverContainerRef.current = cid; setHoverContainer(cid); }
    return ok ? hit : null;
  };
  // react-grid-layout external-drop hooks (library tiles are native HTML5 drags)
  const libDropSize = () => {
    const p = libDragRef.current;
    if (p?.template) { const l = p.template.widget?.layout || {}; return { w: Math.min(cols, l.w || 6), h: l.h || 5 }; }
    return defaultSizeFor(p?.type || "_default", cols);
  };
  const onDropDragOver = (e) => {
    if (!isWidgetDrag(e) && !libDragRef.current) return false;
    if (trackHoverContainer(e.clientX, e.clientY)) return false;   // placeholder lives in the container instead
    return libDropSize();
  };
  const onGridDrop = (layout, item, e) => {
    const p = libDragRef.current || readWidgetDrag(e);
    const cx = e.clientX, cy = e.clientY;
    endLibDrag();
    if (!p || (!p.type && !p.template)) {
      // Not a library widget: a Data-pane field dropped on empty canvas lands at the pointer.
      if (readFieldDrag(e)) dropFieldOnCanvas(e, item ? { x: item.x, y: item.y } : gridPointFromClient(cx, cy));
      return;
    }
    const tpl = p.template || null;
    const hit = containerUnderPoint(cx, cy);
    const cont = hit && widgets.find((w) => w.id === hit.id && w.type === "container");
    if (cont && !tpl) { addWidgetIntoContainer(p.type, cont, hit.el, cx, cy); return; }
    const size = tpl ? { w: Math.min(cols, tpl.widget?.layout?.w || 6), h: tpl.widget?.layout?.h || 5 } : defaultSizeFor(p.type, cols);
    const pos = item ? { x: item.x, y: item.y } : gridPointFromClient(cx, cy, size);
    const pushed = {};
    (layout || []).forEach((l) => { if (l.i !== DROPPING_ID) pushed[l.i] = { x: l.x, y: l.y, w: l.w, h: l.h }; });
    if (tpl) { pasteAnchorRef.current = pos; pasteCountRef.current = 0; addTemplateWidget(tpl); return; }
    addWidget(p.type, pos, pushed);
  };
  // Empty-canvas click: deselect + remember where the next paste should land.
  const onCanvasClick = (e) => {
    if (!editing) return;
    if (e.target.closest && e.target.closest(".react-grid-item, [data-testid='canvas-widget'], button, a, input, textarea, select, [role='dialog']")) return;
    pasteAnchorRef.current = gridPointFromClient(e.clientX, e.clientY);
    pasteCountRef.current = 0;
    clearSelection();
  };

  // Add a fully-configured widget (from AI)
  const addConfiguredWidget = ({ type, title, dataset_id, config }) => {
    const size = SIZE[type] || SIZE._default;
    const maxY = widgets.reduce((m, w) => Math.max(m, (w.layout?.y || 0) + (w.layout?.h || 4)), 0);
    const id2 = newWid();
    updateWidgets([...widgets, { id: id2, type, title, dataset_id, config, layout: { x: 0, y: maxY, w: Math.min(cols, Math.round(size.w * sizeScale)), h: size.h } }]);
    setSelectedIds([id2]);
  };

  // Accepts (id, patch) or a {id: patch} map so several widgets update in one history step.
  const updateWidget = (wid, patch) => {
    const patches = wid && typeof wid === "object" ? wid : { [wid]: patch };
    updateWidgets(widgetsRef.current.map((w) => patches[w.id] ? { ...w, ...patches[w.id], config: patches[w.id].config ? patches[w.id].config : w.config } : w));
  };
  // Item 5: Data-pane field actions (sort / filter) applied to the selected visual
  const fieldAction = (action, p) => {
    const w = widgetsRef.current.find((x) => x.id === selectedIds[0]);
    if (!w) return;
    const cfg = w.config || {};
    if (action === "sort") {
      const m = (cfg.measures || []).find((x) => x && x.field === p.field);
      const d = (cfg.dimensions || []).find((x) => x && x.field === p.field);
      const key = m ? (m.key || m.alias || `${m.agg || "sum"}_${m.field}`) : d ? (d.key || d.alias || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field)) : null;
      if (!key) { toast.info(`Add “${p.field}” to this visual first, then sort by it`); return; }
      updateWidget(w.id, { config: { ...cfg, sort: [{ key, direction: p.direction }] } });
      toast.success(`Sorted by ${p.field} (${p.direction})`);
      return;
    }
    if (action === "filter") {
      if ((cfg.filters || []).some((f) => f && f.field === p.field)) { toast.info(`“${p.field}” is already a filter on this visual — edit it in the Filters section`); return; }
      const op = p.numeric ? "gte" : p.date ? "between" : "in";
      updateWidget(w.id, { config: { ...cfg, filters: [...(cfg.filters || []), { field: p.field, op, value: p.numeric || p.date ? "" : [] }] } });
      toast.success(`Filter on ${p.field} added — set its values in the Filters section`);
    }
  };
  // Item 78: stable inline config patcher for on-canvas editors (rich text) — identity must not change per render
  const inlineConfigPatch = useCallback((wid, patch) => {
    const w = widgetsRef.current.find((x) => x.id === wid);
    if (w) updateWidgets(widgetsRef.current.map((x) => x.id === wid ? { ...x, config: { ...x.config, ...patch } } : x));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // AI drill "Apply" (AiDrillDialog) patches the widget through the normal undo-able path.
  useEffect(() => {
    const h = (e) => {
      const { widgetId, config } = e.detail || {};
      if (widgetId && config && widgetsRef.current.some((w) => w.id === widgetId)) updateWidget(widgetId, { config });
    };
    window.addEventListener("eip:ai-drill-apply", h);
    return () => window.removeEventListener("eip:ai-drill-apply", h);
  });
  const deleteWidgets = (ids) => {
    const rm = new Set(ids);
    // Children of a deleted container are released back to the canvas, not deleted.
    const next = [];
    widgets.forEach((w) => {
      if (rm.has(w.id)) return;
      if (w.parent_id && rm.has(w.parent_id)) {
        const parent = widgets.find((x) => x.id === w.parent_id);
        const pl = parent?.layout || {};
        const lc = w.layout_child || {};
        const nw = Math.min(lc.w || 6, cols);
        next.push({
          ...w, parent_id: null, layout_child: null,
          layout: { x: Math.max(0, Math.min(cols - nw, (pl.x || 0) + (lc.x || 0))), y: (pl.y || 0) + (lc.y || 0), w: nw, h: lc.h || 4 },
        });
      } else next.push(w);
    });
    updateWidgets(next);
    setSelectedIds((s) => s.filter((i) => !rm.has(i)));
  };
  const duplicateWidgets = (ids) => {
    const idSet = new Set(ids);
    // Duplicating a container carries its children along.
    widgets.forEach((w) => { if (w.parent_id && idSet.has(w.parent_id)) idSet.add(w.id); });
    const sel = widgets.filter((w) => idSet.has(w.id));
    if (!sel.length) return;
    const copies = materializePaste(sel, widgets, cols);
    updateWidgets([...widgets, ...copies]);
    setSelectedIds(copies.filter((c) => !c.parent_id).map((c) => c.id));
    toast.success(sel.length === 1 ? "Widget duplicated" : `${sel.length} widgets duplicated`);
  };
  const toggleLock = (w) => updateWidget(w.id, { locked: !w.locked });
  const toggleHidden = (w) => updateWidget(w.id, { hidden: !w.hidden });

  // ---- Data pane drag-drop -------------------------------------------------
  const payloadToConfigAdd = (cfg, p) => {
    const c = JSON.parse(JSON.stringify(cfg || {}));
    c.dimensions = c.dimensions || []; c.measures = c.measures || [];
    if (p.kind === "measure") {
      c.measures = [...c.measures, { key: p.key, field: p.field || "", agg: p.agg || "sum" }];
    } else if (p.kind === "hierarchy") {
      const lv = p.levels || [];
      if (lv.length) {
        c.dimensions = [...c.dimensions, { field: lv[0].field || lv[0], date_part: "none" }];
        c.drilldownPath = lv.slice(1).map((l) => ({ field: l.field || l, date_part: "none" }));
      }
    } else if (["number", "currency", "percent"].includes(p.data_type) && p.default_agg !== "none") {
      c.measures = [...c.measures, { field: p.field, agg: p.default_agg || "sum" }];
      c.adhoc = true;
    } else {
      c.dimensions = [...c.dimensions, { field: p.field, date_part: p.date_part || "none" }];
    }
    return c;
  };
  // Relationship required: a field from another dataset can only join when the Model links them.
  const requireRelationship = (fromId, toId, retry = null) => {
    if (hasRelationshipPath(fromId, toId, contracts)) return true;
    const a = datasets.find((d) => d.id === fromId)?.name || "this widget's dataset";
    const b = datasets.find((d) => d.id === toId)?.name || "that dataset";
    toast.error(`${a} ↔ ${b} aren't linked yet`, {
      id: `rel-${fromId}-${toId}`,
      description: "Match their key fields once — takes a few seconds — and this field will join right away.",
      duration: 10000,
      action: {
        label: "Link them now",
        onClick: () => setJoinPrompt({ leftId: fromId, rightId: toId, retry }),
      },
      cancel: { label: "Model view", onClick: () => window.open(`/model/${fromId}?tab=joins&with=${toId}`, "_blank") },
    });
    return false;
  };
  const dropFieldOnWidget = (w, e) => {
    const p = readFieldDrag(e);
    if (!p) return;
    e.preventDefault(); e.stopPropagation();
    setActiveDataset(p.dataset_id);
    if (["text", "image"].includes(w.type)) { toast.info(`${w.type === "text" ? "Text" : "Image"} widgets don't take data fields`); return; }
    if (w.type === "container") { toast.info("Containers hold widgets, not fields — drop onto a widget inside"); return; }
    if (w.dataset_id && w.dataset_id !== p.dataset_id) {
      // Cross-dataset drop: tag the entry with its dataset — queries run through Model relationships.
      const crossable = !["slicer", "multi_filter", "data_grid", ...FILTER_WIDGET_TYPES].includes(w.type);
      if (!crossable) { toast.error("This widget is bound to a different dataset"); return; }
      if (p.kind === "hierarchy") { toast.error("Hierarchies can't join across datasets"); return; }
      if (p.kind === "measure" && !p.field) { toast.error("Calculated measures can't join across datasets — drag a plain numeric field instead"); return; }
      const joinField = () => {
        const base = widgetsRef.current.find((x) => x.id === w.id) || w;
        const c = JSON.parse(JSON.stringify(base.config || {}));
        c.dimensions = c.dimensions || []; c.measures = c.measures || [];
        const isMeasure = p.kind === "measure" || (["number", "currency", "percent"].includes(p.data_type) && p.default_agg !== "none");
        if (isMeasure) c.measures = [...c.measures, { field: p.field, agg: p.kind === "measure" ? (p.agg || "sum") : (p.default_agg || "sum"), dataset_id: p.dataset_id }];
        else c.dimensions = [...c.dimensions, { field: p.field, date_part: p.date_part || "none", dataset_id: p.dataset_id }];
        c.adhoc = true;
        updateWidgets(widgetsRef.current.map((x) => x.id === w.id ? { ...x, config: c } : x));
        setSelectedIds([w.id]);
        toast.success(`${p.label} joined from ${datasets.find((d) => d.id === p.dataset_id)?.name || "another dataset"} via Model relationships`);
      };
      if (!requireRelationship(w.dataset_id, p.dataset_id, joinField)) return;
      joinField();
      return;
    }
    if (w.type === "slicer" || FILTER_WIDGET_TYPES.includes(w.type)) {
      if (p.kind === "measure") { toast.info("Filters take a field, not a measure"); return; }
      updateWidgets(widgets.map((x) => x.id === w.id ? { ...x, dataset_id: p.dataset_id, config: { ...(x.config || {}), dimensions: [{ field: p.field, date_part: "none" }] } } : x));
      setFwVals((s) => ({ ...s, [w.id]: undefined }));
      toast.success(`${p.label} set as filter field`);
      return;
    }
    if (w.type === "multi_filter") {
      if (p.kind === "measure") { toast.info("Filters take a field, not a measure"); return; }
      updateWidgets(widgets.map((x) => x.id === w.id ? { ...x, dataset_id: p.dataset_id, config: { ...(x.config || {}), fields: [...((x.config || {}).fields || []), { field: p.field, multi: true }] } } : x));
      toast.success(`${p.label} added as a filter`);
      return;
    }
    if (w.type === "data_grid") {
      updateWidgets(widgets.map((x) => x.id === w.id ? { ...x, dataset_id: p.dataset_id } : x));
      toast.success("Dataset bound to the grid — pick columns in the properties panel");
      return;
    }
    updateWidgets(widgets.map((x) => x.id === w.id ? { ...x, dataset_id: p.dataset_id, config: payloadToConfigAdd(x.config, p) } : x));
    setSelectedIds([w.id]);
    toast.success(`${p.label} added to widget`);
  };
  // Field dragged from the Data pane onto empty canvas → new KPI (measure) or bar (dimension),
  // placed at the drop point when one is known (falls back to the bottom).
  const dropFieldOnCanvas = (e, at = null) => {
    const p = readFieldDrag(e);
    if (!p) return;
    e.preventDefault();
    setActiveDataset(p.dataset_id);
    const maxY = widgets.reduce((m, w) => Math.max(m, (w.layout?.y || 0) + (w.layout?.h || 4)), 0);
    const id2 = newWid();
    const isMeasure = p.kind === "measure" || (["number", "currency", "percent"].includes(p.data_type) && p.default_agg !== "none");
    const options = { palette: "Eazy Core", colorByCategory: true, gradient: true, showLegend: true, showGrid: true, showLabels: false, smooth: true };
    const size = isMeasure ? { w: Math.min(cols, Math.round(3 * sizeScale)), h: 3 } : { w: Math.min(cols, Math.round(6 * sizeScale)), h: 5 };
    let layout = clampLayout({ x: at ? at.x : 0, y: at ? at.y : maxY, ...size }, isMeasure ? "kpi" : "bar", cols);
    if (at && freeMode) {
      const placed = resolveFree({ [id2]: layout }, visibleWidgets.map((w) => ({ ...(w.layout || {}), i: w.id })), cols);
      layout = { ...layout, ...placed[id2] };
    }
    const newW = isMeasure
      ? { id: id2, type: "kpi", title: p.label, dataset_id: p.dataset_id, layout,
          config: p.kind === "measure" ? { dimensions: [], measures: [{ key: p.key, field: p.field || "", agg: p.agg || "sum" }], limit: 1000, options } : { dimensions: [], measures: [{ field: p.field, agg: p.default_agg || "sum" }], adhoc: true, limit: 1000, options } }
      : { id: id2, type: "bar", title: `Count by ${p.label}`, dataset_id: p.dataset_id, layout,
          config: { dimensions: p.kind === "hierarchy" ? [{ field: (p.levels?.[0]?.field || p.levels?.[0]) || "", date_part: "none" }] : [{ field: p.field, date_part: p.date_part || "none" }], measures: [{ field: p.field, agg: "count" }], adhoc: true, limit: 1000, options } };
    updateWidgets([...widgets, newW]);
    setSelectedIds([id2]);
    toast.success(`Created ${isMeasure ? "KPI" : "bar chart"} from ${p.label}`);
  };
  const allowFieldDrop = (e) => { if (e.dataTransfer?.types?.includes(FIELD_MIME)) e.preventDefault(); };

  // ---- Format painter: copy visual formatting from one widget onto another ----
  const [fmtPaint, setFmtPaint] = useState(null);
  const copyFormat = (w) => {
    const { subtitle, altText, compare, ...visual } = (w.config?.options || {});
    setFmtPaint({ options: visual, srcId: w.id });
    toast.info("Format copied — click another widget to apply it (Esc cancels)");
  };
  const applyFormatTo = (w) => {
    if (["text", "slicer", "image", "multi_filter"].includes(w.type)) { toast.info("This widget type doesn't take chart formatting"); return; }
    const cfg = w.config || {};
    const keep = {};
    ["subtitle", "altText", "compare"].forEach((k) => { if ((cfg.options || {})[k] !== undefined) keep[k] = cfg.options[k]; });
    updateWidgets(widgets.map((x) => x.id === w.id ? { ...x, config: { ...cfg, options: { ...fmtPaint.options, ...keep } } } : x));
    toast.success("Formatting applied");
  };

  // ---- Multi-select ------------------------------------------------------
  const handleWidgetClick = (w, e) => {
    if (preview) return;
    // Nested widgets (inside containers) must not bubble clicks to the container card.
    if (e && e.stopPropagation) e.stopPropagation();
    if (fmtPaint && w.id !== fmtPaint.srcId) {
      applyFormatTo(w);
      if (!(e && e.shiftKey)) setFmtPaint(null);
      return;
    }
    if (w.dataset_id) setActiveDataset(w.dataset_id);
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey)) {
      setSelectedIds((s) => s.includes(w.id) ? s.filter((i) => i !== w.id) : [...s, w.id]);
    } else {
      setSelectedIds(expandToGroup(widgets, w.id));
    }
  };
  const selectFromLayers = (wid, additive) => {
    if (additive) setSelectedIds((s) => s.includes(wid) ? s.filter((i) => i !== wid) : [...s, wid]);
    else setSelectedIds(expandToGroup(widgets, wid));
  };
  const clearSelection = () => setSelectedIds([]);
  const selectAll = () => setSelectedIds(visibleWidgets.map((w) => w.id));

  const groupSelection = () => {
    if (selectedIds.length < 2) return;
    const gid = newGid();
    updateWidgets(widgets.map((w) => selSet.has(w.id) ? { ...w, group_id: gid } : w));
    toast.success(`Grouped ${selectedIds.length} widgets`);
  };
  const ungroupSelection = () => {
    updateWidgets(widgets.map((w) => selSet.has(w.id) ? { ...w, group_id: null } : w));
    toast.success("Ungrouped");
  };
  const selGroupIds = selectedIds.map((i) => widgets.find((w) => w.id === i)?.group_id).filter(Boolean);
  const allGrouped = selectedIds.length >= 2 && selGroupIds.length === selectedIds.length && new Set(selGroupIds).size === 1;

  // ---- Combine Widget (container) operations ------------------------------
  const MAX_NEST = 3;
  const chainBelow = (id) => {
    const kc = widgets.filter((w) => w.parent_id === id && w.type === "container");
    return kc.length ? 1 + Math.max(...kc.map((k) => chainBelow(k.id))) : 0;
  };
  const chainAbove = (id) => {
    let d = 0, cur = widgets.find((w) => w.id === id);
    while (cur && cur.parent_id) {
      const p = widgets.find((w) => w.id === cur.parent_id);
      if (!p) break;
      if (p.type === "container") d += 1;
      cur = p;
    }
    return d;
  };
  const combineSelection = () => {
    const sel = widgets.filter((w) => selSet.has(w.id));
    if (sel.length < 2) return;
    if (sel.some((w) => w.parent_id)) { toast.error("Widgets already inside a container can't be combined — release them first"); return; }
    if (sel.some((w) => w.type === "container" && chainBelow(w.id) >= MAX_NEST - 1)) {
      toast.error(`Max container nesting is ${MAX_NEST} levels`); return;
    }
    const minX = Math.min(...sel.map((w) => w.layout?.x || 0));
    const minY = Math.min(...sel.map((w) => w.layout?.y || 0));
    const maxX = Math.max(...sel.map((w) => (w.layout?.x || 0) + (w.layout?.w || 6)));
    const maxY = Math.max(...sel.map((w) => (w.layout?.y || 0) + (w.layout?.h || 4)));
    const cid = newWid();
    const cont = {
      id: cid, type: "container", title: "Container", dataset_id: null,
      config: { container: { layout: "free", gap: 8, padding: 8 }, options: {} },
      layout: { x: Math.max(0, minX), y: minY, w: Math.min(cols, maxX - minX), h: (maxY - minY) + 1 },
    };
    const updated = widgets.map((w) => {
      if (!selSet.has(w.id)) return w;
      const L = w.layout || {};
      const cw = Math.min(L.w || 6, 12);
      return {
        ...w, parent_id: cid, group_id: null,
        layout_child: { x: Math.min(Math.max(0, (L.x || 0) - minX), Math.max(0, 12 - cw)), y: Math.max(0, (L.y || 0) - minY), w: cw, h: L.h || 4 },
      };
    });
    updateWidgets([...updated, cont]);
    setSelectedIds([cid]);
    toast.success(`${sel.length} widgets combined — pick a layout in the properties panel`);
  };
  const adoptSelection = (containerId) => {
    const cont = widgets.find((w) => w.id === containerId);
    if (!cont) return;
    const eligible = widgets.filter((w) => {
      if (!selSet.has(w.id) || w.id === containerId || w.parent_id === containerId) return false;
      if (w.type === "container" && chainAbove(containerId) + 2 + chainBelow(w.id) > MAX_NEST) return false;
      return true;
    });
    if (!eligible.length) { toast.info("Select widgets on the canvas first"); return; }
    const ids = new Set(eligible.map((w) => w.id));
    let row = 0;
    updateWidgets(widgets.map((w) => {
      if (!ids.has(w.id)) return w;
      const L = w.layout_child || w.layout || {};
      const cw = Math.min(L.w || 6, 12);
      const lc = { x: 0, y: row, w: cw, h: L.h || 4 };
      row += lc.h;
      return { ...w, parent_id: containerId, group_id: null, layout_child: lc };
    }));
    setSelectedIds([containerId]);
    toast.success(`${eligible.length} widget${eligible.length > 1 ? "s" : ""} moved into the container`);
  };
  const releaseChild = (wid) => {
    const ids = new Set(Array.isArray(wid) ? wid : [wid]);
    let changed = 0;
    const next = widgets.map((x) => {
      if (!ids.has(x.id) || !x.parent_id) return x;
      const parent = widgets.find((p) => p.id === x.parent_id);
      const pl = parent?.layout || { x: 0, y: 0, w: 6, h: 6 };
      const lc = x.layout_child || {};
      const nw = Math.min(lc.w || 6, cols);
      changed += 1;
      return {
        ...x, parent_id: null, layout_child: null,
        layout: { x: Math.max(0, Math.min(cols - nw, (pl.x || 0) + (lc.x || 0))), y: (pl.y || 0) + (pl.h || 6) + (lc.y || 0), w: nw, h: lc.h || 4 },
      };
    });
    if (!changed) return;
    updateWidgets(next);
    toast.success(changed === 1 ? "Widget released to the canvas" : `${changed} widgets released to the canvas`);
  };
  const reorderChildren = (orderMap) => {
    updateWidgets(widgets.map((w) => orderMap[w.id] !== undefined
      ? { ...w, layout_child: { ...(w.layout_child || {}), order: orderMap[w.id] } } : w));
  };
  // Always read the latest widgets: the nested grid reports layouts right after a drop/release
  // and a stale closure would resurrect a child that was just moved out of the container.
  const applyChildLayout = (map) => {
    const cur = widgetsRef.current;
    if (!cur.some((w) => map[w.id] && w.parent_id)) return;
    updateWidgets(cur.map((w) => map[w.id] && w.parent_id ? { ...w, layout_child: { ...(w.layout_child || {}), ...map[w.id] } } : w));
  };

  // ---- Dashboard data sources (attached datasets scope the field pickers) ----
  const attachedSources = useMemo(() => dashboard?.data_sources || [], [dashboard?.data_sources]);
  const scopedDatasets = useMemo(() => {
    if (!attachedSources.length) return datasets;
    const used = new Set(widgets.flatMap(widgetDatasetIds));
    const keep = datasets.filter((d) => attachedSources.includes(d.id) || used.has(d.id));
    return keep.length ? keep : datasets;
  }, [datasets, attachedSources, widgets]);
  const onSourcesChanged = (ds) => setDashboard((d) => ({ ...d, data_sources: ds }));
  const onWidgetsRemoved = (datasetId) => {
    // Mirror the server-side detach(remove_widgets): drop widgets using the source + their children.
    setDashboard((d) => {
      const usedIds = new Set((d.widgets || []).filter((w) => widgetDatasetIds(w).includes(datasetId)).map((w) => w.id));
      return { ...d, widgets: (d.widgets || []).filter((w) => !usedIds.has(w.id) && !usedIds.has(w.parent_id)) };
    });
    setSelectedIds([]);
  };

  const doAlign = (mode) => updateWidgets(alignWidgets(widgets, selectedIds, mode, cols));
  const hideSelection = () => {
    updateWidgets(widgets.map((w) => selSet.has(w.id) ? { ...w, hidden: true } : w));
    setSelectedIds([]);
    toast.success("Hidden — restore from the Layers pane");
  };
  const bulkFormat = (optPatch) => {
    updateWidgets(widgets.map((w) => {
      if (!selSet.has(w.id) || ["text", "slicer"].includes(w.type)) return w;
      const cfg = w.config || {};
      return { ...w, config: { ...cfg, options: { ...(cfg.options || {}), ...optPatch } } };
    }));
    toast.success("Formatting applied");
  };

  // Interactions panel: set how clicks on src affect tgt ("filter" default | "highlight" | "none")
  const [ixSource, setIxSource] = useState(null);
  const saveMasterInteractions = (srcId, patch) => {
    updateWidgets(widgets.map((w) => w.id === srcId ? { ...w, config: { ...(w.config || {}), ...patch } } : w));
    toast.success("Interactions updated");
  };
  // Toolbar quick-switch of a master filter's interaction profile
  const applyInteractionProfile = (wid, pid) => {
    const w = widgets.find((x) => x.id === wid);
    const patch = w ? applyProfilePatch(w, pid) : null;
    if (patch) saveMasterInteractions(wid, patch);
  };

  // ---- Clipboard (cross-dashboard capable) --------------------------------
  const [remapState, setRemapState] = useState(null);  // {missing:[...], ok:[...]}
  const copySelection = () => {
    const idSet = new Set(selectedIds);
    widgets.forEach((w) => { if (w.parent_id && idSet.has(w.parent_id)) idSet.add(w.id); });
    const n = copyWidgetsToClipboard(widgets, [...idSet], datasets, dashboard?.id);
    if (n) toast.success(`${n} widget${n > 1 ? "s" : ""} copied — paste here or in another dashboard`);
  };
  const insertPasted = (list) => {
    const copies = materializePaste(list, widgets, cols);
    const anchor = pasteAnchorRef.current;
    if (anchor) {
      // Land the group where the user last clicked; repeated pastes step down-right.
      const n = pasteCountRef.current++;
      const tops = copies.filter((c) => !c.parent_id);
      let placed = placeGroupAt(tops, { x: Math.min(cols - 1, anchor.x + n), y: anchor.y + n }, cols);
      if (freeMode) placed = resolveFree(placed, visibleWidgets.map((w) => ({ ...(w.layout || {}), i: w.id })), cols);
      tops.forEach((c) => { c.layout = { ...c.layout, ...placed[c.id] }; });
    }
    updateWidgets([...widgets, ...copies]);
    setSelectedIds(copies.map((c) => c.id));
    toast.success(`${copies.length} widget${copies.length > 1 ? "s" : ""} pasted`);
  };
  const pasteClipboard = () => {
    const clip = readWidgetClipboard();
    if (!clip) { toast.info("Nothing to paste"); return; }
    const dsIds = new Set(datasets.map((d) => d.id));
    const ok = clip.widgets.filter((w) => !w.dataset_id || dsIds.has(w.dataset_id));
    const missingWidgets = clip.widgets.filter((w) => w.dataset_id && !dsIds.has(w.dataset_id));
    if (!missingWidgets.length) { insertPasted(ok); return; }
    // Group missing widgets by their (unavailable) source dataset -> remap dialog
    const byDs = {};
    missingWidgets.forEach((w) => {
      if (!byDs[w.dataset_id]) {
        const meta = clip.datasets?.[w.dataset_id] || {};
        byDs[w.dataset_id] = { dataset_id: w.dataset_id, name: meta.name || "Unknown dataset", schema: meta.schema || [], widgets: [] };
      }
      byDs[w.dataset_id].widgets.push(w);
    });
    setRemapState({ missing: Object.values(byDs), ok });
  };
  const confirmRemap = (mappings) => {
    const { missing, ok } = remapState;
    const remapped = missing.flatMap((m) => {
      const cfg = mappings[m.dataset_id];
      return m.widgets.map((w) => remapWidgetDataset(w, cfg.dest_dataset_id, cfg.fieldMap));
    });
    setRemapState(null);
    insertPasted([...ok, ...remapped]);
  };

  // ---- Widget templates (cross-dashboard reuse library) --------------------
  const [tplSaveWidget, setTplSaveWidget] = useState(null);
  const [tplName, setTplName] = useState("");
  const [tplBump, setTplBump] = useState(0);
  const saveTemplate = async () => {
    const w = tplSaveWidget;
    if (!w || !tplName.trim()) return;
    const d = datasets.find((x) => x.id === w.dataset_id);
    try {
      await api.post("/widget-templates", {
        name: tplName.trim(),
        widget: { ...JSON.parse(JSON.stringify(w)), id: undefined, group_id: undefined },
        dataset_meta: d ? { id: d.id, name: d.name, schema: (d.schema || []).map((c) => ({ name: c.name, type: c.type })) } : null,
      });
      setTplSaveWidget(null); setTplName(""); setTplBump((b) => b + 1);
      toast.success("Saved to Templates — available in every dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save template"); }
  };
  const addTemplateWidget = (tpl) => {
    const w = JSON.parse(JSON.stringify(tpl.widget || {}));
    if (w.dataset_id && !datasets.find((d) => d.id === w.dataset_id)) {
      setRemapState({
        missing: [{ dataset_id: w.dataset_id, name: tpl.dataset_meta?.name || "Unknown dataset", schema: tpl.dataset_meta?.schema || [], widgets: [w] }],
        ok: [],
      });
      return;
    }
    insertPasted([w]);
  };

  // ---- Keyboard shortcuts --------------------------------------------------
  const canEdit = !dashboard || (dashboard.my_actions ? dashboard.my_actions.includes("edit") : dashboard.my_role !== "viewer");
  const canPublish = !!dashboard && (dashboard.my_actions ? dashboard.my_actions.includes("publish") : dashboard.my_role === "owner");
  const canShare = !dashboard || (dashboard.my_actions ? dashboard.my_actions.includes("share") : dashboard.my_role !== "viewer");
  const canManage = !!dashboard && (dashboard.my_actions ? dashboard.my_actions.includes("manage_permissions") : dashboard.my_role === "owner");
  const canExport = !dashboard || (dashboard.my_actions ? dashboard.my_actions.includes("export") : true);
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (e.key === "?") { e.preventDefault(); setShortcutsOpen((v) => !v); return; }
      if (mod && k === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if (mod && k === "y") { e.preventDefault(); redo(); return; }
      if (!canEdit || preview) return;
      if (mod && k === "c" && selectedIds.length) { e.preventDefault(); copySelection(); return; }
      if (mod && k === "v") { e.preventDefault(); pasteClipboard(); return; }
      if (mod && k === "d" && selectedIds.length) { e.preventDefault(); duplicateWidgets(selectedIds); return; }
      if (mod && k === "a") { e.preventDefault(); selectAll(); return; }
      if (mod && k === "g") { e.preventDefault(); e.shiftKey ? ungroupSelection() : groupSelection(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedIds.length) { e.preventDefault(); deleteWidgets(selectedIds); return; }
      if (e.key === "Escape") { setFmtPaint(null); clearSelection(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // ---- Layout / group drag -------------------------------------------------
  const dragStartRef = useRef(null);
  const skipLayoutRef = useRef(false);
  const onLayoutChange = (layout) => {
    if (!dashboard || device !== "desktop") return;
    if (skipLayoutRef.current) { skipLayoutRef.current = false; return; }
    const cur = widgetsRef.current;
    const map = {}; layout.forEach((l) => { if (l.i !== DROPPING_ID) map[l.i] = { x: l.x, y: l.y, w: l.w, h: l.h }; });
    const changed = cur.some((w) => { const l = map[w.id]; return l && !w.parent_id && (l.x !== w.layout?.x || l.y !== w.layout?.y || l.w !== w.layout?.w || l.h !== w.layout?.h); });
    if (!changed) return;
    updateWidgets(cur.map((w) => map[w.id] && !w.parent_id ? { ...w, layout: map[w.id] } : w));
  };
  const onDragStart = (layout, oldItem) => { dragStartRef.current = oldItem ? { i: oldItem.i, x: oldItem.x, y: oldItem.y } : null; };
  const resizeStartRef = useRef(null);
  const onResizeStart = (layout, oldItem) => { resizeStartRef.current = oldItem ? { i: oldItem.i, x: oldItem.x, y: oldItem.y, w: oldItem.w, h: oldItem.h } : null; };
  // Grouped widgets resize as one unit: scale mates proportionally around the resized widget's origin.
  const onResizeStop = (layout, oldItem, newItem) => {
    clearGuides();
    const st = resizeStartRef.current; resizeStartRef.current = null;
    if (!st || !newItem || st.i !== newItem.i || !st.w || !st.h) return;
    if (st.w === newItem.w && st.h === newItem.h) return;
    const w0 = widgets.find((w) => w.id === newItem.i);
    const mates = new Set();
    if (w0?.group_id) widgets.forEach((w) => { if (w.group_id === w0.group_id && w.id !== w0.id && !w.hidden) mates.add(w.id); });
    if (selSet.has(newItem.i)) selectedIds.forEach((i) => { if (i !== newItem.i) mates.add(i); });
    if (!mates.size) return;
    const sw = newItem.w / st.w, sh = newItem.h / st.h;
    const layMap = {}; layout.forEach((l) => { layMap[l.i] = l; });
    skipLayoutRef.current = true;
    updateWidgets(widgets.map((w) => {
      if (w.id === newItem.i) return { ...w, layout: { ...(w.layout || {}), x: newItem.x, y: newItem.y, w: newItem.w, h: newItem.h } };
      if (mates.has(w.id) && !w.locked) {
        const Lw = w.layout || {};
        const nw = Math.max(1, Math.min(cols, Math.round((Lw.w || 6) * sw)));
        const nx = Math.max(0, Math.min(cols - nw, Math.round(st.x + ((Lw.x || 0) - st.x) * sw)));
        const ny = Math.max(0, Math.round(st.y + ((Lw.y || 0) - st.y) * sh));
        const nh = Math.max(2, Math.round((Lw.h || 4) * sh));
        return { ...w, layout: { ...Lw, x: nx, y: ny, w: nw, h: nh } };
      }
      const l = layMap[w.id];
      return l ? { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : w;
    }));
  };
  // Item 77.2: drop a dragged widget into a container under the pointer
  const maybeDropIntoContainer = (wid, e, element) => {
    if (!wid || !e) return false;
    const w = widgets.find((x) => x.id === wid);
    if (!w || w.parent_id) return false;
    // Item 2 hardening: dragging one of several selected (or grouped) widgets moves the whole set
    const movingIds = selectedIds.length > 1 && selectedIds.includes(wid) ? selectedIds
      : w.group_id ? widgets.filter((x) => x.group_id === w.group_id).map((x) => x.id) : [wid];
    const cx = e.clientX ?? (e.changedTouches && e.changedTouches[0]?.clientX);
    const cy = e.clientY ?? (e.changedTouches && e.changedTouches[0]?.clientY);
    if (cx == null || cy == null) return false;
    const stack = document.elementsFromPoint(cx, cy);
    const bodyEl = stack.find((el) => el.hasAttribute && el.hasAttribute("data-container-id") && (!element || !element.contains(el)));
    const cid = bodyEl?.getAttribute("data-container-id");
    if (!cid || movingIds.includes(cid)) return false;
    const cont = widgets.find((x) => x.id === cid);
    if (!cont || cont.type !== "container") return false;
    const moving = widgets.filter((x) => movingIds.includes(x.id) && !x.parent_id);
    for (const mw of moving) {
      if (mw.type !== "container") continue;
      let cur = cont;
      while (cur) { if (cur.id === mw.id) return false; cur = widgets.find((x) => x.id === cur.parent_id); }
      if (chainAbove(cid) + 2 + chainBelow(mw.id) > MAX_NEST) { toast.error(`Max container nesting is ${MAX_NEST} levels`); return false; }
    }
    if (!moving.length) return false;
    const ccfg = (cont.config || {}).container || {};
    const L = w.layout || {};
    const { x: gx, y: gy } = containerPointToGrid(cx, cy, bodyEl.getBoundingClientRect(), ccfg, Math.min(L.w || 6, 12), L.h || 4, zoom);
    // Keep the dragged set's relative arrangement: offset each widget from the dragged one
    const place = {};
    moving.forEach((mw) => {
      const ML = mw.layout || {};
      const mwW = Math.min(ML.w || 6, 12);
      place[mw.id] = {
        x: Math.max(0, Math.min(12 - mwW, gx + ((ML.x || 0) - (L.x || 0)))),
        y: Math.max(0, gy + ((ML.y || 0) - (L.y || 0))), w: mwW, h: ML.h || 4,
      };
    });
    skipLayoutRef.current = true;
    updateWidgets(widgets.map((x) => place[x.id]
      ? { ...x, parent_id: cid, group_id: null, layout_child: place[x.id] }
      : x));
    setSelectedIds(moving.length === 1 ? [moving[0].id] : [cid]);
    toast.success(moving.length === 1 ? "Widget moved into the container" : `${moving.length} widgets moved into the container`);
    return true;
  };
  // A child dragged past its container's edge is released onto the canvas (or moved into
  // the container under the pointer). Inside its own container the nested grid handles it.
  const onChildDragStop = (cid, wid, e, el) => {
    hoverContainerRef.current = null; setHoverContainer(null);
    const w = widgets.find((x) => x.id === wid);
    if (!w || !e) return;
    const cx = e.clientX ?? (e.changedTouches && e.changedTouches[0]?.clientX);
    const cy = e.clientY ?? (e.changedTouches && e.changedTouches[0]?.clientY);
    if (cx == null || cy == null) return;
    const hit = containerUnderPoint(cx, cy, el);
    if (hit && hit.id === cid) return;
    const lc = w.layout_child || {};
    if (hit) {
      const other = widgets.find((x) => x.id === hit.id && x.type === "container");
      if (!other || other.id === wid) return;
      if (w.type === "container" && chainAbove(other.id) + 2 + chainBelow(wid) > MAX_NEST) { toast.error(`Max container nesting is ${MAX_NEST} levels`); return; }
      const occ = (other.config || {}).container || {};
      const pos = containerPointToGrid(cx, cy, hit.el.getBoundingClientRect(), occ, Math.min(lc.w || 6, 12), lc.h || 4, zoom);
      skipLayoutRef.current = true;
      updateWidgets(widgets.map((x) => x.id === wid ? { ...x, parent_id: other.id, layout_child: { ...lc, ...pos } } : x));
      setSelectedIds([wid]);
      toast.success("Widget moved to the other container");
      return;
    }
    const canvasEl = document.getElementById("dashboard-capture");
    const r = canvasEl?.getBoundingClientRect();
    if (!r || cx < r.left || cx > r.right || cy < r.top || cy > r.bottom) return;
    const size = { w: Math.min(lc.w || 6, cols), h: lc.h || 4 };
    let layout = clampLayout({ ...gridPointFromClient(cx, cy, size), ...size }, w.type, cols);
    if (freeMode) {
      const placed = resolveFree({ [wid]: layout }, visibleWidgets.map((x) => ({ ...(x.layout || {}), i: x.id })), cols);
      layout = { ...layout, ...placed[wid] };
    }
    skipLayoutRef.current = true;
    updateWidgets(widgets.map((x) => x.id === wid ? { ...x, parent_id: null, layout_child: null, layout } : x));
    setSelectedIds([wid]);
    toast.success("Widget released to the canvas");
  };
  const onDragStop = (layout, oldItem, newItem, placeholder, e, element) => {
    clearGuides();
    hoverContainerRef.current = null; setHoverContainer(null);
    const st = dragStartRef.current; dragStartRef.current = null;
    if (maybeDropIntoContainer(newItem?.i, e, element)) return;
    if (!st || !newItem || st.i !== newItem.i) return;
    const dx = newItem.x - st.x, dy = newItem.y - st.y;
    if (!dx && !dy) return;
    const w0 = widgets.find((w) => w.id === newItem.i);
    const mates = new Set();
    if (w0?.group_id) widgets.forEach((w) => { if (w.group_id === w0.group_id && w.id !== w0.id && !w.hidden) mates.add(w.id); });
    if (selSet.has(newItem.i)) selectedIds.forEach((i) => { if (i !== newItem.i) mates.add(i); });
    if (!mates.size) return;
    const layMap = {}; layout.forEach((l) => { layMap[l.i] = l; });
    skipLayoutRef.current = true;
    updateWidgets(widgets.map((w) => {
      if (mates.has(w.id) && !w.locked) {
        const L = w.layout || {};
        const nw = Math.min(L.w || 6, cols);
        return { ...w, layout: { ...L, x: Math.max(0, Math.min(cols - nw, (L.x || 0) + dx)), y: Math.max(0, (L.y || 0) + dy) } };
      }
      const l = layMap[w.id];
      return l ? { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : w;
    }));
  };

  const gridLayout = useMemo(() => visibleWidgets.map((w) => {
    const min = minSizeFor(w.type);
    return {
      i: w.id, x: w.layout?.x || 0, y: w.layout?.y || 0, w: Math.max(min.w, w.layout?.w || 6), h: Math.max(min.h, w.layout?.h || 5),
      minW: min.w, minH: min.h, static: !!w.locked,
    };
  }), [visibleWidgets]);

  // ---- Mobile layout editing --------------------------------------------
  const mobileMap = useMemo(() => mobileLayoutFor(visibleWidgets), [visibleWidgets]);
  const mobileVisible = visibleWidgets.filter((w) => !w.hidden_mobile);
  const mobileHidden = visibleWidgets.filter((w) => w.hidden_mobile);
  const mobileGridLayout = mobileVisible.map((w) => {
    const l = mobileMap[w.id] || { x: 0, y: 0, w: 4, h: 4 };
    return { i: w.id, x: l.x, y: l.y, w: Math.min(4, l.w || 4), h: l.h || 4, minW: 1, minH: 2 };
  });
  const onMobileLayoutChange = (layout) => {
    if (!dashboard || device !== "mobile") return;
    const map = {}; layout.forEach((l) => { map[l.i] = { x: l.x, y: l.y, w: l.w, h: l.h }; });
    const changed = mobileVisible.some((w) => { const l = map[w.id]; const cur = mobileMap[w.id]; return l && (l.x !== cur?.x || l.y !== cur?.y || l.w !== cur?.w || l.h !== cur?.h); });
    if (!changed) return;
    updateWidgets(widgets.map((w) => map[w.id] ? { ...w, layout_mobile: map[w.id] } : w));
  };
  const setMobileHidden = (w, hidden) => updateWidget(w.id, { hidden_mobile: hidden, ...(hidden ? {} : { layout_mobile: w.layout_mobile || mobileMap[w.id] }) });

  // ---- Canvas sizing / zoom / measurement --------------------------------
  // Callback refs so measurement re-attaches whenever the node remounts
  // (e.g. switching Report -> Data/Model -> Report) — keeps widget sizes exact.
  const scrollRef = useRef(null);
  const scrollRO = useRef(null);
  const innerRO = useRef(null);
  const [containerW, setContainerW] = useState(1200);
  const [containerH, setContainerH] = useState(600);
  const [innerH, setInnerH] = useState(600);
  const setScrollEl = useCallback((el) => {
    scrollRef.current = el;
    if (scrollRO.current) { scrollRO.current.disconnect(); scrollRO.current = null; }
    if (el) {
      scrollRO.current = new ResizeObserver(() => { setContainerW(el.clientWidth); setContainerH(el.clientHeight); });
      scrollRO.current.observe(el);
      setContainerW(el.clientWidth); setContainerH(el.clientHeight);
    }
  }, []);
  const setInnerEl = useCallback((el) => {
    if (innerRO.current) { innerRO.current.disconnect(); innerRO.current = null; }
    if (el) {
      innerRO.current = new ResizeObserver(() => setInnerH(el.offsetHeight));
      innerRO.current.observe(el);
      setInnerH(el.offsetHeight);
    }
  }, []);

  const gridW = fixed ? canvas.width : Math.max(320, Math.round(containerW / zoom));
  // Editing: the canvas (and its drop surface) always fills the visible viewport so new
  // widgets can be dropped anywhere, not only over existing content.
  const canvasMinH = fixed ? canvas.height : (!preview && device === "desktop" ? Math.max(240, Math.round((containerH - 8) / zoom)) : undefined);
  const zoomFit = () => {
    if (!fixed) { setZoom(1); return; }
    setZoom(Math.min(1.5, Math.max(0.2, Math.round(((containerW - 24) / canvas.width) * 100) / 100)));
  };

  // ---- Alignment guides (free-form drag/resize) ---------------------------
  const [guides, setGuides] = useState(null);
  const computeGuides = (layout, _old, item) => {
    if (canvas.layout_mode !== "free") return;
    const v = new Set(), h = new Set();
    layout.filter((l) => l.i !== item.i).forEach((o) => {
      [o.x, o.x + o.w].forEach((gx) => { if (gx === item.x || gx === item.x + item.w) v.add(gx); });
      [o.y, o.y + o.h].forEach((gy) => { if (gy === item.y || gy === item.y + item.h) h.add(gy); });
    });
    setGuides(v.size || h.size ? { v: [...v], h: [...h] } : null);
  };
  const clearGuides = () => setGuides(null);
  const colW = (gridW - MARGIN * (cols - 1) - PAD * 2) / cols;
  const guideX = (gx) => PAD + gx * (colW + MARGIN) - MARGIN / 2;
  const guideY = (gy) => PAD + gy * (ROW_H + MARGIN) - MARGIN / 2;

  const gfDefAll = useMemo(() => dashboard?.global_filters || [], [dashboard]);
  // Item 97 \u00a720: a tab-scoped filter only exists while its tab is open.
  const gfDef = useMemo(() => gfDefAll.filter((f) => !f.tab_id || f.tab_id === effTab), [gfDefAll, effTab]);
  // Item 98 \u2014 FilterPanel: sidebar on desktop, off-canvas drawer under 860px.
  const [narrow, setNarrow] = useState(() => window.innerWidth < 860);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 860);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const filterLayout = canvasAll.filter_layout || "bar";
  const panelSettings = canvasAll.filter_panel || {};
  const showFilterPanel = (filterLayout === "panel" || filterLayout === "both") && gfDef.some(inPanel);
  const showFilterBar = filterLayout !== "panel";
  const [panelOpen, setPanelOpen] = useState(() => !(canvasAll.filter_panel || {}).collapsed);
  const panelDatasetId = (gfDef.find(inPanel) || {}).dataset_id || null;
  const exportFilteredCsv = () => toast.promise(
    downloadFilteredCsv({
      datasetId: panelDatasetId, defs: gfDef.filter(inPanel), values: gfVals,
      filename: `${dashRef.current?.name || "dashboard"}_filtered.csv`,
    }),
    { loading: "Building filtered CSV\u2026", success: "Filtered CSV downloaded", error: "Export failed" });
  useEffect(() => { if (narrow) setPanelOpen(false); }, [narrow]);
  // Dedicated filter widgets (combo box / list box / date picker): value per widget id
  const [fwVals, setFwVals] = useState({});
  // Item 4: restore the viewer's last applied filter state for this dashboard (per user, local).
  const fsRestoredRef = useRef(null);
  useEffect(() => {
    if (!dashboard?.id || fsRestoredRef.current === dashboard.id) return;
    fsRestoredRef.current = dashboard.id;
    const st = loadFilterState(dashboard.id);
    if (!st) return;
    if (st.gf) setGfVals((v) => ({ ...v, ...st.gf }));
    if (st.sl) setSlicerVals(st.sl);
    if (st.fw) setFwVals(st.fw);
  }, [dashboard?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dashboard?.id || fsRestoredRef.current !== dashboard.id) return;
    saveFilterState(dashboard.id, { gf: gfVals, sl: slicerVals, fw: fwVals });
  }, [dashboard?.id, gfVals, slicerVals, fwVals]);
  // Default bookmark: apply the user's saved default view once per dashboard load.
  const bmAppliedRef = useRef(null);
  useEffect(() => {
    if (!dashboard?.id || bmAppliedRef.current === dashboard.id) return;
    bmAppliedRef.current = dashboard.id;
    if (hasFilterState(dashboard.id)) return;   // the user's own last filters win over the default view
    api.get(`/dashboards/${dashboard.id}/bookmarks`).then(({ data: bms }) => {
      const def = (bms || []).find((b) => b.is_default && b.user_id === user?.id);
      if (!def) return;
      setGfVals((v) => ({ ...v, ...(def.gf_vals || {}) }));
      setSlicerVals(def.slicer_vals || {});
      setFwVals(def.fw_vals || {});
      setWhatIfVals(def.what_if || {});
      toast.info(`Applied your default view "${def.name}"`);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard?.id]);
  // Drillthrough context arriving from another dashboard (?dt= param)
  const [drillIn, setDrillIn] = useState(() => decodeDrill(new URLSearchParams(window.location.search).get("dt")));
  const clearDrillIn = () => {
    setDrillIn(null);
    const u = new URL(window.location.href);
    u.searchParams.delete("dt");
    window.history.replaceState({}, "", u.pathname + u.search);
  };
  // Item 13: modal drillthrough renders this page inside a dialog — hide the app chrome
  useEffect(() => {
    setModalChrome(dtModalView);
    return () => setModalChrome(false);
  }, [dtModalView]);
  // Cross-filtering: click a data point on a widget -> filter the others
  const [crossFilters, setCrossFilters] = useState({});   // {widgetId: {dataset_id, field?, key?, value}}
  const toggleCross = (wid, datasetId, dimRef, value) => {
    setCrossFilters((cf) => {
      const cur = cf[wid];
      if (cur && JSON.stringify(cur.value) === JSON.stringify(value) && cur.field === dimRef.field && cur.key === dimRef.key) {
        const n = { ...cf }; delete n[wid]; return n;
      }
      return { ...cf, [wid]: { dataset_id: datasetId, field: dimRef.field, key: dimRef.key, value } };
    });
  };
  const clearCross = (wid) => setCrossFilters((cf) => { const n = { ...cf }; delete n[wid]; return n; });
  // Item 97 \u00a721: unless the author allows cross-tab interactions, only widgets on the open tab
  // may filter the dashboard \u2014 a slicer left on another tab has no effect here.
  const ixWidgets = useMemo(() => {
    if (tabBar.cross_tab_interactions) return widgets;
    const own = new Set(widgets.filter((w) => !w.parent_id && widgetTabId(w, tabs) === effTab).map((w) => w.id));
    return widgets.filter((w) => own.has(w.id) || own.has(w.parent_id));
  }, [widgets, tabs, effTab, tabBar.cross_tab_interactions]);

  // Combine global filter bar + slicer selections into one dataset-scoped active filter list
  const activeFilters = useMemo(() => {
    const fromBar = toActiveFilters(gfDef, gfVals);
    const ixIds = new Set(ixWidgets.map((w) => w.id));
    const fromSlicers = slicerFilters(slicerVals, ixWidgets);
    const fromCross = Object.entries(crossFilters).filter(([wid]) => ixIds.has(wid)).map(([wid, cf]) => ({
      dataset_id: cf.dataset_id, field: cf.field, key: cf.key, op: Array.isArray(cf.value) ? "in" : "eq", value: cf.value, __src: wid,
    }));
    const fromFilterWidgets = ixWidgets
      .filter((w) => FILTER_WIDGET_TYPES.includes(w.type))
      .flatMap((w) => filterWidgetToFilters(w, fwVals[w.id]));
    const fromDrill = (drillIn?.f || []).map((f) => ({ ...f, __dt: true }));
    // Item 13: "override" replaces this dashboard's own filter values on the carried fields
    const overridden = drillIn?.mode === "override" ? new Set(fromDrill.map((f) => f.field)) : null;
    const keep = (list) => (overridden ? list.filter((f) => !overridden.has(f.field)) : list);
    // Item 30: parameters bound to a field filter the whole dashboard
    const fromParams = paramFilters(canvas.what_if || [], whatIfVals);
    return [...keep(fromParams), ...keep(fromBar), ...keep(fromSlicers), ...fromCross, ...keep(fromFilterWidgets), ...fromDrill];
  }, [gfDef, gfVals, slicerVals, crossFilters, fwVals, ixWidgets, drillIn, canvas.what_if, whatIfVals]);

  const addGlobalFilter = (f) => { setDashboard((d) => ({ ...d, global_filters: [...(d.global_filters || []), f] })); markDirty(); };
  const updateGlobalFilter = (fid, patch) => { setDashboard((d) => ({ ...d, global_filters: (d.global_filters || []).map((x) => x.id === fid ? { ...x, ...patch } : x) })); markDirty(); };
  const removeGlobalFilter = (fid) => { setDashboard((d) => ({ ...d, global_filters: (d.global_filters || []).filter((x) => x.id !== fid) })); setGfVals((v) => { const n = { ...v }; delete n[fid]; return n; }); markDirty(); };
  const setSlicer = (datasetId, field, value) => setSlicerVals((s) => ({ ...s, [`${datasetId}::${field}`]: value }));
  const clearAllFilters = () => { setGfVals({}); setSlicerVals({}); setCrossFilters({}); setFwVals({}); };
  const filterSets = useFilterSets({
    dashboardId: dashboard?.id, tabId: effTab, values: gfVals,
    onApply: (vals) => { setGfVals(vals); setCrossFilters({}); }, enabled: !!dashboard?.id,
  });
  const onGfChange = (fid, v) => setGfVals((s) => clearDescendants(gfDef, { ...s, [fid]: v }, fid));

  // Item 20: the dashboard's workspace (site) regional layer (tenant → workspace → user)
  const { setSiteScope } = useRegional();
  useEffect(() => { if (dashboard?.site_id) setSiteScope(dashboard.site_id); }, [dashboard?.site_id, setSiteScope]);

  // Item 13: state-preserving drillthrough navigation. This dashboard publishes a snapshot of its
  // filter state; Back / a trail step reloads the source with `restore=<key>` and re-applies it.
  useEffect(() => registerNavState(() => ({ gf: gfVals, sl: slicerVals, fw: fwVals, y: window.scrollY })),
    [gfVals, slicerVals, fwVals]);
  const restoreKey = new URLSearchParams(window.location.search).get("restore");
  useEffect(() => {
    if (!restoreKey) return;
    const st = readNavState(restoreKey);
    if (st) {
      if (st.gf) setGfVals(st.gf);
      if (st.sl) setSlicerVals(st.sl);
      if (st.fw) setFwVals(st.fw);
      applyScroll(st.y);
    }
    const u = new URL(window.location.href);
    u.searchParams.delete("restore");
    window.history.replaceState({}, "", u.pathname + u.search);
  }, [restoreKey]);
  const goDrillStep = (step) => {
    if (!step?.path) return;
    const sep = step.path.includes("?") ? "&" : "?";
    window.location.assign(`${step.path}${step.bk ? `${sep}restore=${step.bk}` : ""}`);
  };
  const drillBack = () => {
    const trail = drillIn?.trail || [];
    const last = trail[trail.length - 1];
    if (last?.path) return goDrillStep(last);
    if (drillIn?.src) return nav(`/builder/${drillIn.src}`);
    return window.history.back();
  };

  // Briefly pulse all widgets whenever active filters change (filter-apply feedback)
  const [pulse, setPulse] = useState(false);
  const activeKey = JSON.stringify(activeFilters);
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) { firstFilterRun.current = false; return; }
    setPulse(true);
    const t = setTimeout(() => setPulse(false), 680);
    return () => clearTimeout(t);
  }, [activeKey]);

  const share = async () => {
    try { const { data } = await api.post(`/dashboards/${dashboard.id}/share`); setDashboard((d) => ({ ...d, is_public: true, public_token: data.public_token })); setShareOpen(true); }
    catch { toast.error("Failed to share"); }
  };

  const [refreshToken, setRefreshToken] = useState(0);
  const [wRefresh, setWRefresh] = useState({});
  const refreshWidget = (wid) => setWRefresh((m) => ({ ...m, [wid]: (m[wid] || 0) + 1 }));
  // Source-specific refresh: only widgets that read from this dataset re-query.
  const refreshSource = (datasetId) => {
    const hit = widgets.filter((w) => widgetDatasetIds(w).includes(datasetId)).map((w) => w.id);
    setWRefresh((m) => { const n = { ...m }; hit.forEach((wid) => { n[wid] = (n[wid] || 0) + 1; }); return n; });
  };
  // Replace source: the server already persisted the rewired widgets — mirror them locally.
  const onSourceReplaced = (data) => {
    const d = data?.dashboard;
    if (!d) return;
    pushHistory(widgetsRef.current);
    setDashboard((cur) => ({ ...cur, widgets: d.widgets || [], data_sources: d.data_sources || [], updated_at: d.updated_at }));
    setSelectedIds([]);
    setRefreshToken((t) => t + 1);
  };
  const [refreshing, setRefreshing] = useState(false);
  const liveDatasetIds = useMemo(() => {
    const used = new Set(widgets.map((w) => w.dataset_id).filter(Boolean));
    return datasets.filter((d) => d.mode === "live" && used.has(d.id)).map((d) => d.id);
  }, [widgets, datasets]);
  const doRefreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all(liveDatasetIds.map((id) => api.post(`/datasets/${id}/refresh`).catch(() => {})));
      setRefreshToken((t) => t + 1);
      toast.success("Live data refreshed");
    } catch { toast.error("Refresh failed"); }
    finally { setRefreshing(false); }
  };
  const publicUrl = dashboard?.public_token ? `${window.location.origin}/view/${dashboard.public_token}` : "";

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [exportThisTabOnly, setExportThisTabOnly] = useState(false);
  const downloadExport = async (fmt) => {
    const p = (async () => {
      const applied = (activeFilters || []).filter((f) => f && f.field).map(({ __scope, __src, ...r }) => r); // __dt kept: export header labels drill context
      const res = await api.post(`/dashboards/${dashboard.id}/export`,
        { applied_filters: applied, what_if_params: whatIfEff || {}, tab_ids: exportThisTabOnly ? [effTab] : [] },
        { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers["content-disposition"] || "").split('filename="')[1]?.replace('"', "") || `${dashboard.name}.${fmt === "excel" ? "xlsx" : fmt === "csv" ? "zip" : fmt === "html" ? "html" : fmt === "pptx" ? "pptx" : "pdf"}`;
      a.click(); URL.revokeObjectURL(url);
    })();
    toast.promise(p, { loading: "Generating export…", success: "Export downloaded", error: "Export failed" });
  };

  const exportWidgetData = async (w, fmt) => {
    // Item 26: single shared export path (styled Excel for every widget type, current filter context)
    const appliedFilters = [...(w.config?.filters || []), ...interactionFilters(w, activeFilters, widgets, datasets).filter((f) => f && f.field)];
    return exportWidget({ widget: w, fmt, filters: appliedFilters, whatIfParams: whatIfEff });
  };

  // ---- Command palette (Ctrl/Cmd+K) --- must be BEFORE any early return to keep hook order stable
  const paletteCommands = useMemo(() => buildBuilderCommands({
    dashboard, preview, chatOpen, canEdit, canShare, canPublish, hasUnpublished, status,
    hasLiveData: liveDatasetIds.length > 0,
    doSave, doRefreshData, share, doPublish, openPublishDialog, doUnpublish, downloadExport,
    togglePreview: () => { setPreview((p) => !p); setSelectedIds([]); },
    setAiOpen, setAddFilterOpen, setCanvasOpen, setInteractionsOpen, setDevice, setChatOpen,
    setCommentsOpen, setAccessOpen, setEmbedOpen, setVersionsOpen, setScheduleOpen, setShortcutsOpen,
    jumpToWidget: (wid) => {
      setWsView("report"); setSelectedIds([wid]);
      setTimeout(() => document.getElementById(`wcap-${wid}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 140);
    },
    goHome: () => nav("/"),
    // action callbacks are stable enough for a command list; deps track visible state only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [dashboard, preview, chatOpen, canEdit, canShare, canPublish, hasUnpublished, status, liveDatasetIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!dashboard) return <div className="min-h-screen flex items-center justify-center bi-canvas"><Loading label="Loading builder" /></div>;

  const editing = !preview && device === "desktop" && !archived && canEdit;
  const freeMode = canvas.layout_mode === "free";
  const zoomRight = preview ? 24 : (rightPanel.collapsed ? 28 : rightPanel.width) + (chatOpen ? 340 : 0) + 24;

  // Whole-surface widget dragging: a plain click stays a click (chart legends,
  // cross-filter, tooltips untouched); once the pointer moves past a small
  // threshold with the button held, a synthetic mousedown on the widget's
  // grid drag handle hands the gesture over to react-grid-layout.
  const bodyDragStart = (w, grip = "widget-drag") => (e) => {
    if (!editing || w.locked || e.button !== 0) return;
    if (e.target.closest && e.target.closest(DRAG_IGNORE_SEL)) return;
    const inHeader = e.target.closest && e.target.closest(".widget-header-drag");
    const bodyOk = !DRAG_BODY_EXCLUDE.has(w.type) && !w.config?.options?.dataZoom;
    if (!inHeader && !bodyOk) return;
    const handle = e.currentTarget.closest(".react-grid-item")?.querySelector("." + grip);
    if (!handle) return;
    const sx = e.clientX, sy = e.clientY;
    const cleanup = () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("mouseup", cleanup, true);
    };
    const onMove = (me) => {
      if (Math.abs(me.clientX - sx) + Math.abs(me.clientY - sy) < DRAG_THRESHOLD_PX) return;
      cleanup();
      handle.dispatchEvent(new MouseEvent("mousedown", { bubbles: true, cancelable: true, clientX: sx, clientY: sy, button: 0 }));
    };
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("mouseup", cleanup, true);
  };

  const datasetHasField = (dsId, field) => {
    const d = datasets.find((x) => x.id === dsId);
    if (!d) return true;
    return (d.schema || []).some((c) => c.name === field) || (d.calc_fields || []).some((f) => f.name === field);
  };

  const renderWidget = (w0, { mobile = false, depth = 0 } = {}) => {
    // Item 101: an un-applied AI restyle is previewed here only \u2014 saved config is untouched.
    const w = themePreview ? themedWidgets([w0], themePreview)[0] : w0;
    const gripClass = depth === 0 ? "widget-drag" : `child-drag-${depth}`;
    if (w.type === "container") {
      const kids = widgets.filter((x) => x.parent_id === w.id && !x.hidden);
      return (
        <WidgetCard
          testid="canvas-widget"
          widgetType="container"
          headerDrag={!preview && !archived && canEdit && !w.locked ? gripClass : false}
          onMouseDown={bodyDragStart(w, gripClass)}
          selected={!preview && selSet.has(w.id)}
          onClick={(e) => handleWidgetClick(w, e)}
          title={resolveParamTokens(w.title, whatIfEff) || undefined}
          subtitle={w.config?.options?.subtitle || undefined}
          titleStyle={w.config?.options?.titleStyle}
          hideHeader={!!w.config?.options?.hideHeader}
          appearance={w.config?.options?.appearance}
          ariaLabel={w.config?.options?.altText || w.title || "Container"}
          className={cn(w.locked && !preview && "opacity-95", !preview && !selSet.has(w.id) && kids.some((k) => selSet.has(k.id)) && "ring-1 ring-dashed ring-[color:var(--brand-ocean)]/60")}
          actions={!preview && (
            <span className="flex items-center gap-0.5">
              {!mobile && (
                <>
                  <button data-testid="widget-duplicate-btn" title="Duplicate container with its widgets" onClick={(e) => { e.stopPropagation(); duplicateWidgets([w.id]); }}
                    className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><CopyPlus className="w-3.5 h-3.5" /></button>
                  <button data-testid="widget-lock-btn" title={w.locked ? "Unlock position" : "Lock position"} onClick={(e) => { e.stopPropagation(); toggleLock(w); }}
                    className={cn("p-0.5", w.locked ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>
                    {w.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                  </button>
                </>
              )}
              {mobile && (
                <button data-testid="widget-hide-mobile-btn" title="Hide on mobile" onClick={(e) => { e.stopPropagation(); setMobileHidden(w, true); }}
                  className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><EyeOff className="w-3.5 h-3.5" /></button>
              )}
              {!w.locked && <span className={cn(gripClass, "cursor-move text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}><GripVertical className="w-4 h-4" /></span>}
            </span>
          )}
        >
          <ContainerBody widget={w} childWidgets={kids}
            editing={editing && !mobile} depth={depth + 1}
            dropActive={!!editing && hoverContainer === w.id}
            onChildDragStop={onChildDragStop}
            onChildLayout={applyChildLayout}
            onAdoptSelection={() => adoptSelection(w.id)}
            selectedCount={selectedIds.filter((i) => i !== w.id && widgets.find((x) => x.id === i)?.parent_id !== w.id).length}
            renderChild={(k) => (
              <div className="h-full w-full" onDragOver={editing ? allowFieldDrop : undefined}
                onDrop={editing ? (e) => dropFieldOnWidget(k, e) : undefined}>
                {renderWidget(k, { mobile, depth: depth + 1 })}
              </div>
            )} />
        </WidgetCard>
      );
    }
    const crossW = isCrossDatasetWidget(w);
    // drillthrough context only applies to widgets whose dataset actually has the field
    const wFilters = drillIn
      ? activeFilters.filter((f) => !f.__dt || datasetHasField(w.dataset_id, f.field)).map(({ __dt, ...r }) => r)
      : activeFilters;
    const split = splitInteractionFilters(w, wFilters, widgets, datasets, crossW);
    const mfValues = w.type === "multi_filter" && w.dataset_id
      ? Object.fromEntries(Object.entries(slicerVals).filter(([k]) => k.startsWith(`${w.dataset_id}::`)).map(([k, v]) => [k.split("::")[1], v]))
      : undefined;
    const noDataActions = ["text", "slicer", "image", "multi_filter", "data_grid", "combo_box", "list_box", "date_picker"].includes(w.type);
    const dragFromBody = editing && !mobile && !w.locked && !DRAG_BODY_EXCLUDE.has(w.type) && !w.config?.options?.dataZoom;
    return (
    <WidgetCard
      testid="canvas-widget"
      widgetType={w.type}
      headerDrag={!preview && !archived && canEdit && !w.locked ? gripClass : false}
      onMouseDown={bodyDragStart(w, gripClass)}
      selected={!preview && selSet.has(w.id)}
      onClick={(e) => handleWidgetClick(w, e)}
      title={resolveParamTokens(w.title, whatIfEff) || undefined}
      subtitle={w.config?.options?.subtitle || undefined}
      titleStyle={w.config?.options?.titleStyle}
      subtitleStyle={w.config?.options?.subtitleStyle}
      description={w.config?.options?.description || undefined}
      hideHeader={!!w.config?.options?.hideHeader}
      appearance={w.config?.options?.appearance}
      ariaLabel={w.config?.options?.altText || w.title || w.type}
      className={cn(pulse ? "filter-pulse" : undefined, dragFromBody && "widget-anydrag", w.locked && !preview && "opacity-95", fmtPaint && !preview && w.id !== fmtPaint.srcId && "cursor-copy ring-1 ring-[color:var(--brand-mint)]/50",
        interactionsOpen && ixSource && !preview && (w.id === ixSource
          ? "ring-2 ring-[#2B4EFF]"
          : (w.dataset_id && !["text", "image"].includes(w.type))
            ? ({ none: "opacity-40", highlight: "ring-2 ring-[#FF6B35]/70" }[(widgets.find((x) => x.id === ixSource)?.config?.interactions || {})[w.id]] || "ring-2 ring-[#2B4EFF]/40")
            : undefined))}
      actions={preview ? (
        <WidgetViewActions widget={w} filters={[...(w.config?.filters || []), ...interactionFilters(w, activeFilters, widgets, datasets).filter((f) => f && f.field)]}
          whatIfParams={whatIfEff} crossDataset={!!crossW} canExport={canExport}
          onUnderlying={(w2, fls) => setUnderlying({ widget: w2, filters: fls })} />
      ) : (
        <span className="flex items-center gap-0.5">
          {!mobile && (
            <>
              {w.dataset_id && !noDataActions && (
                <button data-testid="widget-refresh-btn" title="Refresh this widget's data" onClick={(e) => { e.stopPropagation(); refreshWidget(w.id); }}
                  className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><RefreshCw className="w-3.5 h-3.5" /></button>
              )}
              {w.dataset_id && !noDataActions && !crossW && (
                <button data-testid="widget-underlying-btn" title="View underlying data (or right-click a data point)" onClick={(e) => { e.stopPropagation(); setUnderlying({ widget: w, filters: [...(w.config?.filters || []), ...interactionFilters(w, activeFilters, widgets, datasets).filter((f) => f.field)] }); }}
                  className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><Table2 className="w-3.5 h-3.5" /></button>
              )}
              {w.dataset_id && !noDataActions && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="widget-export-btn" title="Export this widget" onClick={(e) => e.stopPropagation()}
                      className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><Download className="w-3.5 h-3.5" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem data-testid="widget-export-png" onClick={() => exportWidgetData(w, "png")}><FileImage className="w-4 h-4 mr-2" />PNG image</DropdownMenuItem>
                    <DropdownMenuItem data-testid="widget-export-pdf" onClick={() => exportWidgetData(w, "pdf")}><FileText className="w-4 h-4 mr-2" />PDF snapshot</DropdownMenuItem>
                    {!crossW && <DropdownMenuItem data-testid="widget-export-csv" onClick={() => exportWidgetData(w, "csv")}><Download className="w-4 h-4 mr-2" />Data as CSV</DropdownMenuItem>}
                    {!crossW && <DropdownMenuItem data-testid="widget-export-excel" onClick={() => exportWidgetData(w, "excel")}><Download className="w-4 h-4 mr-2" />Data as Excel</DropdownMenuItem>}
                    {!crossW && <DropdownMenuItem data-testid="widget-export-json" onClick={() => exportWidgetData(w, "json")}><Code2 className="w-4 h-4 mr-2" />Data as JSON</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <button data-testid="widget-duplicate-btn" title="Duplicate" onClick={(e) => { e.stopPropagation(); duplicateWidgets([w.id]); }}
                className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><CopyPlus className="w-3.5 h-3.5" /></button>
              {tabs.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="widget-tab-btn" title="Move or copy to another tab" onClick={(e) => e.stopPropagation()}
                      className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><LayersIcon className="w-3.5 h-3.5" /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    {tabs.filter((t) => t.id !== widgetTabId(w, tabs)).map((t) => (
                      <DropdownMenuItem key={`mv-${t.id}`} data-testid={`widget-move-tab-${t.id}`} onClick={() => moveWidgetToTab(w, t.id, false)}>
                        <ArrowRightLeft className="w-4 h-4 mr-2" />Move to “{t.name}”
                      </DropdownMenuItem>
                    ))}
                    {tabs.filter((t) => t.id !== widgetTabId(w, tabs)).map((t) => (
                      <DropdownMenuItem key={`cp-${t.id}`} data-testid={`widget-copy-tab-${t.id}`} onClick={() => moveWidgetToTab(w, t.id, true)}>
                        <CopyPlus className="w-4 h-4 mr-2" />Copy to “{t.name}”
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {!["text", "slicer", "image", "multi_filter"].includes(w.type) && (
                <button data-testid="widget-format-painter-btn" title={fmtPaint?.srcId === w.id ? "Format copied — click a target widget" : "Format painter: copy this widget's formatting"}
                  onClick={(e) => { e.stopPropagation(); fmtPaint?.srcId === w.id ? setFmtPaint(null) : copyFormat(w); }}
                  className={cn("p-0.5", fmtPaint?.srcId === w.id ? "text-[color:var(--brand-mint)]" : "text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]")}>
                  <Paintbrush className="w-3.5 h-3.5" /></button>
              )}
              <button data-testid="widget-save-template-btn" title="Save as template (reuse in other dashboards)" onClick={(e) => { e.stopPropagation(); setTplSaveWidget(w); setTplName(w.title || ""); }}
                className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]"><LayoutTemplate className="w-3.5 h-3.5" /></button>
              <button data-testid="widget-lock-btn" title={w.locked ? "Unlock position" : "Lock position"} onClick={(e) => { e.stopPropagation(); toggleLock(w); }}
                className={cn("p-0.5", w.locked ? "text-[color:var(--brand-ocean)]" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>
                {w.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
              </button>
            </>
          )}
          {mobile && (
            <button data-testid="widget-hide-mobile-btn" title="Hide on mobile" onClick={(e) => { e.stopPropagation(); setMobileHidden(w, true); }}
              className="p-0.5 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><EyeOff className="w-3.5 h-3.5" /></button>
          )}
          {!w.locked && <span className={cn(gripClass, "cursor-move text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}><GripVertical className="w-4 h-4" /></span>}
        </span>
      )}
    >
      <WidgetErrorBoundary dashboardId={dashboard.id} widgetId={w.id} widgetType={w.type} resetKey={JSON.stringify(w.config || {}) + refreshToken}>
        <WidgetRenderer widget={w} globalFilters={split.query} highlightFilters={split.highlight} chartFont={canvas.chart_font} lang={canvas.language}
          onBandDrill={(w2, bandDim, value, fls) => setUnderlying({ widget: w2, filters: (fls || []).filter((f) => f.field), band: { dim: bandDim, value } })}
          refreshToken={`${refreshToken}:${wRefresh[w.id] || 0}`}
          pivotEngine={canvas.pivot_engine}
          whatIfParams={whatIfEff}
          whatIfBaseline={whatIfBase}
          whatIfCompare={whatIfCompare}
          whatIfDefs={canvas.what_if || []}
          whatIfScenarios={canvas.scenarios || []}
          onConfigPatch={canEdit && !preview ? inlineConfigPatch : null}
          dashboardId={dashboard.id}
          dashboardName={dashboard.name}
          drillTrail={drillIn?.trail || null}
          multiFilterValues={mfValues}
          onDrillthrough={(filters) => setUnderlying({ widget: w, filters })}
          slicerValue={slicerVals[`${w.dataset_id}::${w.config?.dimensions?.[0]?.field}`]}
          filterValue={fwVals[w.id]}
          onFilterChange={(val) => setFwVals((s) => ({ ...s, [w.id]: val }))}
          onSlicerChange={(field, value) => setSlicer(w.dataset_id, field, value)}
          onCrossFilter={(dimRef, value) => toggleCross(w.id, w.dataset_id, dimRef, value)}
          onConfigChange={(patch) => updateWidget(w.id, { config: { ...(w.config || {}), ...patch } })} />
      </WidgetErrorBoundary>
    </WidgetCard>
    );
  };

  // Item 101: the AI restyle preview also repaints the canvas itself.
  const vCanvas = themePreview ? themedCanvas(canvas, themePreview) : canvas;

  // Item 97: the tab bar. Authors see every tab (incl. hidden ones); viewers only what they may open.
  const viewTabs = allowedTabs;
  const tabsEl = tabBar.show !== false && (viewTabs.length > 1 || (!preview && canEdit && !archived)) ? (
    <DashboardTabs
      dashboardId={dashboard.id} tabs={viewTabs} activeId={effTab} onSelect={selectTab}
      bar={tabBar} counts={tabCounts} mobile={device === "mobile" || narrow}
      editable={!preview && canEdit && !archived}
      onAdd={() => runTabOps([{ op: "create_tab", name: `Tab ${tabs.length + 1}` }])}
      onReorder={(order) => runTabOps([{ op: "reorder_tabs", order }])}
      onManage={() => setTabsOpen(true)} />
  ) : null;
  const vertBar = tabBar.position === "left" || tabBar.position === "right";

  const desktopCanvas = (
    <div style={{ width: Math.round(gridW * zoom), height: Math.round(innerH * zoom) }} className={fixed ? "mx-auto my-4" : ""}>
      <div id="dashboard-capture" ref={setInnerEl} onClick={onCanvasClick} data-testid="dashboard-canvas"
        role={tabs.length > 1 ? "tabpanel" : undefined}
        aria-label={tabs.length > 1 ? `${curTab?.name} tab` : undefined}
        style={{
          transform: zoom !== 1 ? `scale(${zoom})` : undefined, transformOrigin: "top left",
          width: gridW, minHeight: canvasMinH,
          backgroundColor: vCanvas.bg_color || undefined,
        }}
        className={cn("relative", fixed && "rounded-xl border border-[color:var(--glass-border)] shadow-[var(--shadow-md)] bg-[color:var(--canvas)] overflow-hidden")}>
        {vCanvas.wallpaper_url && (
          <div aria-hidden className="absolute inset-0 pointer-events-none bg-cover bg-center"
            style={{ backgroundImage: `url(${vCanvas.wallpaper_url})`, opacity: vCanvas.wallpaper_opacity }} />
        )}
        {guides && editing && (
          <div className="absolute inset-0 pointer-events-none z-30">
            {guides.v.map((gx) => <div key={`v${gx}`} style={{ left: guideX(gx) }} className="absolute top-0 bottom-0 w-px bg-[color:var(--brand-ocean)] opacity-70" />)}
            {guides.h.map((gy) => <div key={`h${gy}`} style={{ top: guideY(gy) }} className="absolute left-0 right-0 h-px bg-[color:var(--brand-ocean)] opacity-70" />)}
          </div>
        )}
        {visibleWidgets.length === 0 && !libDrag ? (
          <div className="h-full min-h-[420px] flex items-center justify-center relative">
            <EmptyState icon={LayoutGrid} title={tabCounts[effTab] === 0 && widgets.length ? `\u201c${curTab?.name}\u201d is empty` : widgets.length ? "All widgets are hidden" : "Empty canvas"} description={widgets.length ? "Restore widgets from the Layers pane on the left." : "Add widgets from the library on the left, or click “Ask AI” to generate a chart from a question."}
              action={!widgets.length && <Button onClick={() => setAiOpen(true)} className="bg-[color:var(--brand-mint)] text-white"><Sparkles className="w-4 h-4 mr-1" />Ask AI to build a chart</Button>} />
          </div>
        ) : (
          <GridLayout key={effTab} className="layout relative" layout={gridLayout} width={gridW} cols={cols}
            rowHeight={ROW_H} margin={[MARGIN, MARGIN]} containerPadding={[PAD, PAD]}
            compactType={freeMode ? null : "vertical"} preventCollision={freeMode} allowOverlap={false}
            isDraggable={editing} isResizable={editing} resizeHandles={RESIZE_HANDLES}
            draggableHandle=".widget-drag" draggableCancel={DRAG_CANCEL_SEL} transformScale={zoom}
            isDroppable={editing} droppingItem={{ i: DROPPING_ID, w: 6, h: 5 }}
            onDropDragOver={onDropDragOver} onDrop={onGridDrop}
            style={editing ? { minHeight: canvasMinH || 240 } : undefined}
            onDrag={(lay, oldItem, item, ph, e, el) => { computeGuides(lay, oldItem, item); if (e) trackHoverContainer(e.clientX, e.clientY, el, item ? [item.i] : []); }}
            onDragStart={onDragStart} onDragStop={onDragStop} onResize={computeGuides} onResizeStart={onResizeStart} onResizeStop={onResizeStop}
            onLayoutChange={onLayoutChange}>
            {visibleWidgets.map((w) => <div key={w.id} id={`wcap-${w.id}`} data-testid={`widget-${w.id}`} onDragOver={allowFieldDrop} onDrop={(e) => dropFieldOnWidget(w, e)}>{renderWidget(w)}</div>)}
          </GridLayout>
        )}
      </div>
    </div>
  );

  const mobileEditor = (
    <div className="py-6 flex flex-col items-center gap-4">
      <div className="text-xs text-[color:var(--ink-3)] flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" />Mobile layout — arrange, resize and hide widgets for phones. Saved separately from desktop.</div>
      <div data-testid="mobile-editor-frame" className="w-[410px] rounded-[2.2rem] border-[10px] border-neutral-800 bg-[color:var(--canvas)] shadow-2xl overflow-hidden">
        <div className="h-6 bg-neutral-800 flex items-center justify-center"><div className="w-16 h-1.5 rounded-full bg-neutral-600" /></div>
        <div className="max-h-[68vh] overflow-y-auto" style={{ backgroundColor: vCanvas.bg_color || undefined }}>
          {mobileVisible.length === 0 ? (
            <div className="py-16 text-center text-xs text-[color:var(--ink-3)]">All widgets are hidden on mobile</div>
          ) : (
            <GridLayout className="layout" layout={mobileGridLayout} width={390} cols={4}
              rowHeight={48} margin={[10, 10]} containerPadding={[10, 10]}
              compactType="vertical" isDraggable={!preview} isResizable={!preview}
              resizeHandles={["s", "e", "se"]} draggableHandle=".widget-drag" draggableCancel={DRAG_CANCEL_SEL}
              onLayoutChange={onMobileLayoutChange}>
              {mobileVisible.map((w) => <div key={w.id}>{renderWidget(w, { mobile: true })}</div>)}
            </GridLayout>
          )}
        </div>
      </div>
      {mobileHidden.length > 0 && (
        <div data-testid="mobile-hidden-list" className="w-[410px] rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-3">
          <div className="text-[11px] text-[color:var(--ink-3)] mb-2 flex items-center gap-1"><EyeOff className="w-3 h-3" />Hidden on mobile</div>
          <div className="space-y-1.5">
            {mobileHidden.map((w) => (
              <div key={w.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded-lg bg-[color:var(--surface-2)]">
                <span className="truncate text-[color:var(--ink-2)]">{w.title || w.type}</span>
                <button data-testid="widget-show-mobile-btn" onClick={() => setMobileHidden(w, false)} className="text-[color:var(--brand-ocean)] hover:underline flex items-center gap-1"><Eye className="w-3 h-3" />Show</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const restoreLatestVersion = async () => {
    const { data: versions } = await api.get(`/dashboards/${id}/versions`);
    if (!versions?.length) throw new Error("No saved versions to restore");
    await api.post(`/dashboards/${id}/versions/${versions[0].id}/restore`);
    window.location.reload();
  };

  return (
    <DashboardErrorBoundary dashboardId={id} onRestore={restoreLatestVersion}>
    <div className="h-screen flex flex-col bi-canvas overflow-hidden">
      <div data-chrome={dtModalView ? "topbar" : undefined} className="contents">
      <BuilderTopbar ctx={{
        dashboard, saveState, status, canEdit, canShare, canPublish, canManage, canExport, hasUnpublished, preview,
        wsView, setWsView, device, setDevice, chatOpen, setChatOpen,
        undo, redo, undoDisabled: undoRef.current.length === 0, redoDisabled: redoRef.current.length === 0,
        hasLiveData: liveDatasetIds.length > 0, refreshing, doRefreshData,
        gfVals, slicerVals, fwVals, whatIfVals, setPerfOpen, setA11yOpen,
        onApplyBookmark: (b) => { setGfVals(b.gf_vals || {}); setSlicerVals(b.slicer_vals || {}); setFwVals(b.fw_vals || {}); setWhatIfVals(b.what_if || {}); setCrossFilters({}); },
        onNameChange: (name) => { setDashboard((d) => ({ ...d, name })); markDirty(); },
        togglePreview: () => { setPreview((p) => !p); setSelectedIds([]); },
        goHome: () => nav("/"), doSave, share,
        setPaletteOpen, setShortcutsOpen, setAiOpen, setAddFilterOpen, setCanvasOpen,
        setInteractionsOpen, setCommentsOpen, setAccessOpen, setEmbedOpen, setVersionsOpen,
        setScheduleOpen, downloadExport, doPublish, openPublishDialog, doUnpublish,
        widgets, applyInteractionProfile,
        setTabsOpen, tabCount: tabs.length, exportThisTabOnly, setExportThisTabOnly,
      }} />
      </div>

      {gfDef.length > 0 && (showFilterBar || (showFilterPanel && (!panelOpen || narrow))) && (
        <div className={`shrink-0 px-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] ${canvas.filter_bar?.sticky ? "sticky top-0 z-30" : ""}`}>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {showFilterBar && (
                <FilterBar filtersDef={gfDef} values={gfVals} onChange={onGfChange} editable={!preview && canEdit} onRemove={removeGlobalFilter} onClearAll={clearAllFilters}
                  widgets={widgets}
                  settings={canvas.filter_bar || {}}
                  onSettingsChange={!preview && canEdit ? (patch) => updateCanvas({ filter_bar: { ...(canvas.filter_bar || {}), ...patch } }) : undefined}
                  onUpdateDef={updateGlobalFilter}
                  onApplyAll={(vals) => setGfVals(vals)} />
              )}
              {showFilterPanel && (!panelOpen || narrow) && (
                <div className="py-2">
                  <FilterPanelToggle open={panelOpen} onOpenChange={setPanelOpen} drawer={narrow}
                    count={toActiveFilters(gfDef.filter(inPanel), gfVals).length} />
                </div>
              )}
            </div>
            {!preview && canEdit && (
              <div className="py-2">
                <FilterLayoutSwitch value={filterLayout} onChange={(v) => updateCanvas({ filter_layout: v })} />
              </div>
            )}
          </div>
        </div>
      )}

      <WhatIfBar defs={canvas.what_if || []} values={whatIfVals} onChange={setWhatIfVals}
        editable={canEdit && !preview} onSaveDefs={(defs) => updateCanvas({ what_if: defs })}
        scenarios={canvas.scenarios || []}
        onSaveScenarios={canEdit && !preview ? (s) => updateCanvas({ scenarios: s }) : null}
        compare={whatIfCompare} onCompareChange={setWhatIfCompare}
        aiEnabled={canEdit} dashboardId={dashboard.id}
        datasets={datasets}
        goalSeekCtx={{ widgets, getQuery: (w) => buildSemanticQuery({ ...(w.config || {}), dimensions: [] }, interactionFilters(w, activeFilters, widgets, datasets)) }}
        getScenarioLink={(s) => dashboard.is_public && dashboard.public_token
          ? `${window.location.origin}/view/${dashboard.public_token}?scenario=${encodeURIComponent(s.id || s.name)}` : null} />

      <DrillthroughBar drill={drillIn} onClear={clearDrillIn} onBack={drillIn ? drillBack : null} onStep={goDrillStep} />

      {Object.keys(crossFilters).length > 0 && (
        <div data-testid="crossfilter-bar" className="shrink-0 px-4 py-1.5 border-b border-[color:var(--glass-border)] bg-[color:rgba(43,78,255,0.06)] flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-[color:var(--ink-3)]">Cross-filters</span>
          {Object.entries(crossFilters).map(([wid, cf]) => (
            <span key={wid} data-testid="crossfilter-chip" className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[color:var(--surface)] border border-[color:rgba(43,78,255,0.35)] text-[color:var(--ink-2)]">
              <span className="text-[color:var(--ink-3)]">{widgets.find((w) => w.id === wid)?.title || "Widget"}:</span>
              <span className="font-medium">{cf.key || cf.field}</span> = {Array.isArray(cf.value) ? `${cf.value.length} selected` : String(cf.value)}
              <button data-testid="crossfilter-clear" onClick={() => clearCross(wid)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] ml-0.5"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button onClick={() => setCrossFilters({})} className="text-[11px] text-[color:var(--brand-ocean)] hover:underline">Clear all</button>
        </div>
      )}

      {tabsEl && !vertBar && tabBar.position !== "bottom" && wsView === "report" && (
        <div className="shrink-0">{tabsEl}</div>
      )}

      <div className="flex-1 min-h-0 min-w-0 flex">
        {wsView === "data" && !preview ? (
          <DataViewPanel datasets={datasets} defaultDatasetId={activeDatasetId}
            onDatasetsChanged={() => { api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); setRefreshToken((t) => t + 1); }} />
        ) : wsView === "model" && !preview ? (
          <ModelWorkspacePanel datasets={datasets} defaultDatasetId={activeDatasetId}
            onContractsChanged={() => setRefreshToken((t) => t + 1)}
            onDatasetsChanged={() => { api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); setRefreshToken((t) => t + 1); }} />
        ) : (
        <>
        {/* Library / Layers */}
        {!preview && device === "desktop" && (
          <ResizableAside side="left" panel={leftPanel} min={170} max={420} testid="builder-left-panel" className="hidden md:block">
            <div className="h-full flex flex-col">
              <div className="shrink-0 px-2 pt-2 flex items-center gap-1 flex-wrap">
                {[{ v: "widgets", label: "Widgets", icon: LayoutGrid }, { v: "data", label: "Data", icon: Database }, { v: "sources", label: "Sources", icon: Server }, { v: "layers", label: "Layers", icon: ListTree }].map((t) => (
                  <button key={t.v} data-testid={`left-tab-${t.v}`} onClick={() => setLeftTab(t.v)}
                    className={cn("flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg transition-colors",
                      leftTab === t.v ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]")}>
                    <t.icon className="w-3 h-3" />{t.label}
                  </button>
                ))}
              </div>
              <div className="flex-1 min-h-0">
                {leftTab === "widgets"
                  ? <WidgetLibrary onDragStart={startLibDrag} onDragEnd={endLibDrag} onAdd={addWidget} recentBump={recentBump} onAddTemplate={addTemplateWidget} templatesBump={tplBump} />
                  : leftTab === "data"
                  ? <DataPane datasets={scopedDatasets} usedDatasetIds={[...new Set(widgets.flatMap(widgetDatasetIds))]}
                      selectedWidget={selected} onFieldAction={fieldAction}
                      onDatasetsChanged={() => { api.get("/datasets").then(({ data }) => setDatasets(data)).catch(() => {}); setRefreshToken((t) => t + 1); }} />
                  : leftTab === "sources"
                  ? <DataSourcesPanel dashboardId={dashboard.id} datasets={datasets} attached={attachedSources}
                      widgets={widgets} activeDatasetId={activeDatasetId} onPickActive={setActiveDataset}
                      onSourcesChanged={onSourcesChanged} onWidgetsRemoved={onWidgetsRemoved}
                      onRefreshSource={refreshSource} onSourceReplaced={onSourceReplaced} canEdit={canEdit}
                      onSelectWidget={(wid) => setSelectedIds([wid])} />
                  : <LayersPane widgets={widgets} selectedIds={selectedIds} onSelect={selectFromLayers}
                      onToggleHidden={toggleHidden} onToggleLock={toggleLock} />}
              </div>
            </div>
          </ResizableAside>
        )}

        {tabsEl && vertBar && tabBar.position === "left" && tabsEl}

        <CreateRelationshipDialog
          open={!!joinPrompt} onOpenChange={(v) => { if (!v) setJoinPrompt(null); }}
          leftDataset={datasets.find((d) => d.id === joinPrompt?.leftId) || null}
          rightDataset={datasets.find((d) => d.id === joinPrompt?.rightId) || null}
          onCreated={async () => {
            const retry = joinPrompt?.retry;
            setJoinPrompt(null);
            await loadContracts();
            if (retry) retry();
          }} />

        {/* Item 98 \u2014 reusable filter panel */}
        {showFilterPanel && (
          <div className={narrow ? "" : "shrink-0 py-2 pl-2"}>
            <FilterPanel
              filtersDef={gfDef} values={gfVals} onChange={(fid, v) => onGfChange(fid, v)}
              onClearAll={clearAllFilters} editable={!preview && canEdit}
              settings={panelSettings} tabId={effTab}
              open={narrow ? panelOpen : panelOpen} onOpenChange={setPanelOpen} drawer={narrow}
              sets={filterSets.sets} onApplySet={filterSets.applySet}
              onSaveSet={filterSets.saveSet} onDeleteSet={filterSets.deleteSet}
              widgets={widgets.filter((w) => !w.parent_id)} onUpdateDef={!preview && canEdit ? updateGlobalFilter : null}
              onApplyAll={(vals) => { setGfVals(vals); setCrossFilters({}); }}
              onExportCsv={panelDatasetId ? exportFilteredCsv : null} />
          </div>
        )}

        {/* Canvas */}
        <div className="flex-1 min-w-0 overflow-auto relative" ref={setScrollEl}
          onDragOver={editing ? allowFieldDrop : undefined} onDrop={editing ? dropFieldOnCanvas : undefined}>
          {editing && selectedIds.length >= 2 && (
            <MultiSelectToolbar count={selectedIds.length} allGrouped={allGrouped}
              onAlign={doAlign} onGroup={groupSelection} onUngroup={ungroupSelection} onCombine={combineSelection}
              onMoveInto={adoptSelection} containers={widgets.filter((w) => w.type === "container" && !selSet.has(w.id) && !w.parent_id)}
              onDuplicate={() => duplicateWidgets(selectedIds)} onHide={hideSelection}
              onDelete={() => deleteWidgets(selectedIds)} onBulkFormat={bulkFormat} onClear={clearSelection} />
          )}
          {device === "mobile" && !preview ? mobileEditor : desktopCanvas}

          {/* Zoom controls */}
          {device === "desktop" && (
            <div data-testid="zoom-controls" className="fixed bottom-4 z-40 flex items-center gap-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface)] shadow-lg px-1.5 py-1"
              style={{ right: zoomRight }}>
              <button data-testid="zoom-out-btn" title="Zoom out" onClick={() => setZoom((z) => Math.max(0.2, Math.round((z - 0.1) * 10) / 10))} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ZoomOut className="w-4 h-4" /></button>
              <button data-testid="zoom-reset-btn" title="Reset zoom" onClick={() => setZoom(1)} className="text-[11px] font-medium text-[color:var(--ink-2)] w-11 text-center">{Math.round(zoom * 100)}%</button>
              <button data-testid="zoom-in-btn" title="Zoom in" onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 10) / 10))} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ZoomIn className="w-4 h-4" /></button>
              {fixed && <button data-testid="zoom-fit-btn" title="Fit to screen" onClick={zoomFit} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--ink)] border-l border-[color:var(--glass-border)] ml-0.5 pl-1.5"><Maximize className="w-4 h-4" /></button>}
            </div>
          )}
        </div>

        {tabsEl && vertBar && tabBar.position === "right" && tabsEl}

        {/* AI Chat panel (conversational builder) */}
        {!preview && chatOpen && canEdit && (
          <aside data-testid="builder-chat-aside" data-chrome="pane" className="w-[340px] shrink-0 border-l border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] backdrop-blur-md hidden md:flex flex-col">
            <DashboardChatPanel
              datasetIds={Array.from(new Set([
                ...(activeDatasetId ? [activeDatasetId] : []),
                ...widgets.map((w) => w.dataset_id).filter(Boolean),
              ])).slice(0, 4)}
              currentWidgets={widgets}
              onWidgetsUpdate={(next) => {
                const { widgets: safe, dropped } = mergeAiWidgets(widgets, next, cols);
                if (dropped) toast.warning(`${dropped} AI widget${dropped > 1 ? "s" : ""} looked invalid and ${dropped > 1 ? "were" : "was"} skipped`);
                const now = Date.now();
                if (now - aiCheckpointRef.current > 600000) {
                  aiCheckpointRef.current = now;
                  api.post(`/dashboards/${id}/versions`, { note: "Before AI chat changes" }).catch(() => {});
                }
                updateWidgets(safe);
              }}
              placeholderPrompts={[
                "Add a KPI card for total revenue",
                "Change the bar chart to a line chart",
                "Remove the last table and add a top-5 categories view",
                "Add a monthly trend of sales",
              ]}
            />
          </aside>
        )}

        {/* Inspector / Properties panel */}
        {!preview && (
          <ResizableAside side="right" panel={rightPanel} min={250} max={560} testid="builder-right-panel" className="hidden lg:block backdrop-blur-md">
            <Inspector widget={selected} multiCount={selectedIds.length} datasets={datasets}
              sourceDatasets={scopedDatasets} widgets={widgets} dashboardFilters={gfDef}
              onUpdate={updateWidget} onDelete={(wid) => deleteWidgets([wid])}
              onReleaseChild={releaseChild} onReorderChildren={reorderChildren}
              onChildLayout={applyChildLayout} onUpdateWidget={updateWidget} />
          </ResizableAside>
        )}
        </>
        )}
      </div>

      {tabsEl && !vertBar && tabBar.position === "bottom" && wsView === "report" && (
        <div className="shrink-0">{tabsEl}</div>
      )}

      <AiDrawer open={aiOpen} onOpenChange={setAiOpen} datasets={datasets} defaultDatasetId={activeDatasetId} onAddWidget={addConfiguredWidget}
        theme={canEdit && !archived ? {
          scope: aiThemeScope, onScopeChange: setAiThemeScope, widget: selected, canvas,
          onPreview: setThemePreview, onApply: applyAiTheme, onDiscard: () => setThemePreview(null),
          onUndo: undoAiTheme, preview: themePreview, canUndo: !!themeUndo,
        } : null} />

      <TabsManagerDialog open={tabsOpen} onOpenChange={setTabsOpen} dashboardId={dashboard.id}
        tabs={tabs} widgets={widgets} bar={tabBar} canEdit={canEdit && !archived} cols={cols}
        onApplyOps={applyTabsResult} onBarChange={setTabBar} activeId={effTab} onSelect={selectTab} />
      <CommentsPanel open={commentsOpen} onOpenChange={setCommentsOpen} dashboardId={dashboard.id} />
      <PerfAnalyzerPanel open={perfOpen} onOpenChange={setPerfOpen} dashboardId={dashboard.id} onRefreshAll={() => setRefreshToken((t) => t + 1)} />
      <A11yAuditPanel open={a11yOpen} onOpenChange={setA11yOpen} dashboard={dashboard} initialReport={a11yReport}
        onSelectWidget={(wid) => { setSelectedIds([wid]); setA11yOpen(false); }} />

      <CanvasSettingsDialog open={canvasOpen} onOpenChange={setCanvasOpen} canvas={canvas} onChange={updateCanvas} onGridColsChange={changeGridCols}
        onApplyTheme={applyTheme} currentPalette={widgets.find((w) => w.config?.options?.palette)?.config.options.palette || "Eazy Core"} />
      <ShortcutSheet open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
      <InteractionsPanel open={interactionsOpen} onOpenChange={(v) => setInteractionsOpen(v)}
        widgets={widgets.filter((w) => !w.hidden)} datasets={datasets} onSaveMaster={saveMasterInteractions} sourceId={ixSource} onSourceChange={setIxSource} />
      <UnderlyingDataDialog open={!!underlying} onOpenChange={(v) => { if (!v) setUnderlying(null); }} ctx={underlying} />
      <VersionHistoryDialog open={versionsOpen} onOpenChange={setVersionsOpen} dashboardId={dashboard.id}
        onRestored={(d) => setDashboard((cur) => ({ ...cur, ...d }))} />

      <PublishNoteDialog open={publishNoteOpen} onOpenChange={setPublishNoteOpen} dashboardId={dashboard.id}
        note={publishNote} setNote={setPublishNote} onPublish={doPublish} />

      <ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} publicUrl={publicUrl}
        shareStats={shareStats} dashboardId={dashboard.id} dashboardName={dashboard.name} />

      <AccessDialog open={accessOpen} onOpenChange={setAccessOpen} dashboardId={dashboard.id}
        onOwnerChanged={(d) => setDashboard((x) => ({ ...x, owner_email: d.owner_email, my_actions: d.my_actions || x.my_actions }))} />
      <ScheduleDialog open={scheduleOpen} onOpenChange={setScheduleOpen} dashboardId={dashboard.id} />
      <EmbedDialog open={embedOpen} onOpenChange={setEmbedOpen} dashboard={dashboard} onUpdate={(patch) => setDashboard((d) => ({ ...d, ...patch }))} />
      <AddFilterDialog open={addFilterOpen} onOpenChange={setAddFilterOpen} datasets={datasets} onAdd={addGlobalFilter} existingFilters={gfDef}
        filterLayout={filterLayout} onFilterLayoutChange={(v) => updateCanvas({ filter_layout: v })}
        tabs={tabs} activeTabId={effTab} />
      <DatasetRemapDialog open={!!remapState} onOpenChange={(v) => !v && setRemapState(null)}
        missing={remapState?.missing || []} datasets={datasets} onConfirm={confirmRemap} />

      {fmtPaint && !preview && (
        <div data-testid="format-painter-hint" className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-[color:var(--ink)] text-white text-xs px-4 py-2 shadow-lg">
          <Paintbrush className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />
          <span>Format copied — click a widget to paste it. Hold <b>Shift</b> to paste to several · <b>Esc</b> cancels</span>
          <button data-testid="format-painter-hint-close" onClick={() => setFmtPaint(null)} className="ml-1 opacity-80 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} commands={paletteCommands} />

      <TemplateSaveDialog widget={tplSaveWidget} name={tplName} setName={setTplName}
        onClose={() => setTplSaveWidget(null)} onSave={saveTemplate} />
    </div>
    </DashboardErrorBoundary>
  );
}

